import { useEffect, useRef } from 'react';

const PARTICLE_COUNT = 210;
const PERSPECTIVE    = 800;
const BASE_RADIUS    = 195;
const NEURAL_COLORS  = ['#4285F4', '#EA4335', '#FBBC05', '#34A853'];
const LOGO_COLORS    = ['#ee2a7b', '#f576b0', '#d42870', '#ff5c96'];
const COLOR_IDX      = Object.fromEntries(NEURAL_COLORS.map((c, i) => [c, i]));

const MAX_SEGS = 5000;
const segData  = NEURAL_COLORS.map(() => new Float32Array(MAX_SEGS * 5));
const segCount = new Int32Array(4);

const hexToRgb = (hex) => {
  if (hex === '#4285F4') return [66, 133, 244];
  if (hex === '#EA4335') return [234, 67, 53];
  if (hex === '#FBBC05') return [251, 188, 5];
  if (hex === '#34A853') return [52, 168, 83];
  if (hex === '#ee2a7b') return [238, 42, 123];
  if (hex === '#f576b0') return [245, 118, 176];
  if (hex === '#d42870') return [212, 40, 112];
  if (hex === '#ff5c96') return [255, 92, 150];
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0,255,65];
};

const lerp = (a, b, t) => a + (b - a) * t;

const LOGO_PATH  = 'M105,34.34L87.17,.26l39.56,11.54c10.12,3.58,19.86,7.37,29.47,12.05l-.07,72.15c0,7.12-1.51,13.6-3.75,20.17-5.67,16.62-17.74,31.38-31.08,42.16-.51-.17-.77-.54-1.12-1.15l-18.15-31.56-37.79-66.8-8.88-15.51-5.13-8.97s-12.82,4.04-22.23,7l.23,55.41c.03,6.6,2.9,12.91,6.04,18.45,5.16,9.09,12.6,15.57,21.02,21.58l.06-46.34,13.61,23.31,33.98,58.04-24.48,14.99-17.65-10.69-7.52-4.77c-19.14-12.38-39.71-30.35-47.84-52.29C2.46,110.97,.21,102.87,.19,94.12l-.19-69.9,10.1,-4.62c10.35,-4.73,20.98,-7.92,31.88,-11.27L69.12,0l5.79,10.61,15.61,28.1,1.23,2.2,29.75,53.76,7.12,13.29,.05,-66.22Z';
const LOGO_SVG_W = 156.2;
const LOGO_SVG_H = 186.8;

// Layered-sine organic noise (Perlin-like, no external dep)
const noise1 = (t, s) =>
  Math.sin(t * 1.27 + s)           * 0.50 +
  Math.sin(t * 2.83 + s * 1.713)   * 0.30 +
  Math.sin(t * 5.17 + s * 3.101)   * 0.20;

const FORMATIONS = {
  DNA_HELIX: (() => {
    const pts = [];
    const perStrand = Math.floor(PARTICLE_COUNT * 0.4);
    const R = 80, HEIGHT = 180;
    for (let i = 0; i < perStrand; i++) {
      const t = (i / perStrand) * Math.PI * 4;
      const y = -HEIGHT + (i / perStrand) * HEIGHT * 2;
      pts.push({ x: R * Math.cos(t), y, z: R * Math.sin(t), role: 'strand0' });
    }
    for (let i = 0; i < perStrand; i++) {
      const t = (i / perStrand) * Math.PI * 4 + Math.PI;
      const y = -HEIGHT + (i / perStrand) * HEIGHT * 2;
      pts.push({ x: R * Math.cos(t), y, z: R * Math.sin(t), role: 'strand1' });
    }
    const RUNGS = 12;
    for (let i = 0; i < RUNGS; i++) {
      const y = -HEIGHT + (i / RUNGS) * HEIGHT * 2;
      pts.push({ x: 0, y, z: 0, role: 'rung' });
    }
    while (pts.length < PARTICLE_COUNT) pts.push({ x: (Math.random() - 0.5) * R * 0.4, y: (Math.random() - 0.5) * HEIGHT, z: 0, role: 'rung' });
    return pts;
  })(),

  EKG: (() => {
    const pts = [];
    const W = 280, H = 90;
    const getY = (nx) => {
      if (nx < 0.10) return Math.sin(nx * Math.PI / 0.10) * 12;
      if (nx < 0.28) return 0;
      if (nx < 0.33) return -Math.sin((nx - 0.28) / 0.05 * Math.PI) * 8;
      if (nx < 0.38) return Math.sin((nx - 0.33) / 0.05 * Math.PI) * H;
      if (nx < 0.43) return -Math.sin((nx - 0.38) / 0.05 * Math.PI) * 14;
      if (nx < 0.56) return 0;
      if (nx < 0.72) return Math.sin((nx - 0.56) / 0.16 * Math.PI) * 28;
      return 0;
    };
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const nx = i / (PARTICLE_COUNT - 1);
      const x = -W / 2 + nx * W;
      pts.push({ x, y: -getY(nx), z: 0, role: 'trace' });
    }
    return pts;
  })(),

  ATOM: (() => {
    const pts = [];
    const nucleus = Math.floor(PARTICLE_COUNT * 0.12);
    const perOrbit = Math.floor((PARTICLE_COUNT - nucleus) / 3);
    const R1 = 120, R2 = 95;
    for (let i = 0; i < nucleus; i++) {
      const t = Math.random() * Math.PI * 2;
      const r = Math.sqrt(Math.random()) * 18;
      pts.push({ x: r * Math.cos(t), y: r * Math.sin(t), z: (Math.random() - 0.5) * 18, role: 'nucleus' });
    }
    for (let orbit = 0; orbit < 3; orbit++) {
      const angle = (orbit / 3) * Math.PI;
      const cosA = Math.cos(angle), sinA = Math.sin(angle);
      for (let i = 0; i < perOrbit; i++) {
        const t = (i / perOrbit) * Math.PI * 2;
        const ex = R1 * Math.cos(t), ey = R2 * Math.sin(t);
        pts.push({ x: ex * cosA, y: ey, z: ex * sinA, role: 'electron' });
      }
    }
    while (pts.length < PARTICLE_COUNT) pts.push({ x: (Math.random() - 0.5) * 18, y: (Math.random() - 0.5) * 18, z: 0, role: 'nucleus' });
    return pts;
  })(),

  BINARY_TREE: (() => {
    const pts = [];
    const branch = (x, y, angle, length, depth) => {
      if (depth > 6 || pts.length >= PARTICLE_COUNT) return;
      const ex = x + Math.sin(angle) * length;
      const ey = y - Math.cos(angle) * length;
      const steps = Math.max(2, Math.floor(length / 8));
      for (let i = 0; i <= steps && pts.length < PARTICLE_COUNT; i++) {
        const t = i / steps;
        pts.push({ x: x + (ex - x) * t, y: y + (ey - y) * t, z: 0, role: depth < 2 ? 'trunk' : (depth < 4 ? 'branch' : 'leaf') });
      }
      const newLen = length * 0.66;
      branch(ex, ey, angle - 0.42, newLen, depth + 1);
      branch(ex, ey, angle + 0.42, newLen, depth + 1);
    };
    branch(0, 140, 0, 100, 0);
    while (pts.length < PARTICLE_COUNT) pts.push({ x: (Math.random() - 0.5) * 30, y: -60 + (Math.random() - 0.5) * 60, z: 0, role: 'leaf' });
    return pts;
  })(),
};

const SCENE_AFFINITIES = {
  LOGO:          { curiosity: 0.3, restlessness: 0.1, playfulness: 0.7 },
  PONG:          { curiosity: 0.1, restlessness: 0.6, playfulness: 0.6 },
  ARKANOID:      { curiosity: 0.2, restlessness: 0.5, playfulness: 0.6 },
  WALLE:         { curiosity: 0.6, restlessness: 0.2, playfulness: 0.4 },
  MONOLITH:      { curiosity: 0.8, restlessness: 0.1, playfulness: 0.0 },
  MATRIX:        { curiosity: 0.7, restlessness: 0.3, playfulness: 0.1 },
  DNA_HELIX:     { curiosity: 0.9, restlessness: 0.0, playfulness: 0.1 },
  EKG:           { curiosity: 0.6, restlessness: 0.2, playfulness: 0.0 },
  ATOM:          { curiosity: 0.7, restlessness: 0.1, playfulness: 0.1 },
  BINARY_TREE:   { curiosity: 0.6, restlessness: 0.0, playfulness: 0.2 },
};

const NeuralSentry = ({ visible, excludeRef }) => {
  const canvasRef     = useRef(null);
  const particles     = useRef([]);
  const waves         = useRef([]);
  const visibleRef    = useRef(visible);
  const startRef      = useRef(null);
  const stopRef       = useRef(null);
  const frame         = useRef(0);
  const logoPointsRef = useRef(null);
  const logoHitRef    = useRef(null);

  const state = useRef({
    mode: 'RESTING',
    timer: 0,
    rotationX: 0,        rotationY: 0,
    targetRotX: 0,       targetRotY: 0,
    pulseFactor: 1.0,    targetPulseFactor: 1.0,
    homingStr: 0.12,     targetHomingStr: 0.12,
    friction: 0.945,     targetFriction: 0.945,
    synapseAlpha: 0.20,  targetSynapseAlpha: 0.20,
    synapseMaxDist: 100, targetSynapseMaxDist: 100,
    rotSpeed: 0.003,     targetRotSpeed: 0.003,
    boidSep: 0.0,        targetBoidSep: 0.0,
    boidAli: 0.0,        targetBoidAli: 0.0,
    boidCoh: 0.0,        targetBoidCoh: 0.0,
    boidRadius: 55,      targetBoidRadius: 55,
    globalEnergy: 0,
    burstPower: 0,
    logoStrength: 0,
    pongStrength: 0,
    arkanoidStrength: 0,
    walleStrength: 0,
    monolithStrength: 0,
    matrixStrength: 0,
    formationStrength: 0,
    currentFormation: null,
    mood: { curiosity: 0.3, restlessness: 0.2, playfulness: 0.1, vigilance: 0.0 },
    pong: { bx: 0, by: 0, bvx: 2.8, bvy: 1.2, leftY: 0, rightY: 0, leftErrPhase: 0, rightErrPhase: 0 },
    matrix: { streams: [] },
    arkanoid: { bx: 0, by: 0, bvx: 0, bvy: 0, paddleX: 0, paddleErrPhase: 0, bricks: null },
    walle: { eyeAngle: 0, eyeTarget: 0.25, eyePause: 0, shootTimer: 480 + Math.random() * 240, shootX: 0, shootY: 0, shootActive: false },
    sceneHistory: [],
    // Organic living systems
    pseudopods: [],
    pseudopodTimer: 120,
    blobMorph: { mode: 'none', strength: 0, targetStrength: 0, axis: { x: 0, y: 1, z: 0 }, phase: 0, timer: 0 },
    hoverLureTimer: 0,
    // Shoal drift: organism wanders like a school of fish and returns to center
    shoal: { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, tx: 0, ty: 0, tz: 0, timer: 100, returning: false },
    transitionAge: 0,
    // Bubble surface deformation: 5 pressure centers wandering on the sphere
    deformCenters: Array.from({ length: 5 }, () => {
      const th = Math.random() * Math.PI * 2;
      const ph = (Math.random() - 0.5) * Math.PI;
      return {
        theta: th, phi: ph,
        dTheta: (Math.random() - 0.5) * 0.0014,
        dPhi:   (Math.random() - 0.5) * 0.0009,
        cx: Math.cos(ph) * Math.cos(th),
        cy: Math.sin(ph),
        cz: Math.cos(ph) * Math.sin(th),
        amp:      (Math.random() * 2 - 1) * 20,
        ampPhase:  Math.random() * Math.PI * 2,
        ampFreq:   0.004 + Math.random() * 0.008,
        curAmp: 0,
      };
    }),
  });

  const mouse = useRef({ x: 0, y: 0, speed: 0, active: false, leaveTimer: 0, angularVel: 0, prevAngle: 0 });

  const initOrganism = () => {
    const np = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi   = (Math.random() - 0.5) * Math.PI;
      const r     = BASE_RADIUS * (0.82 + Math.random() * 0.36);
      const cosP  = Math.cos(phi), sinP = Math.sin(phi);
      const cosT  = Math.cos(theta), sinT = Math.sin(theta);
      const x = r * cosP * cosT, y = r * sinP, z = r * cosP * sinT;
      const ci = Math.floor(Math.random() * 4);
      np.push({
        x, y, z,
        vx: 0, vy: 0, vz: 0,
        ox: x, oy: y, oz: z,
        originRadius: r,
        theta, cosP, sinP, cosT, sinT,
        size: Math.random() * 1.8 + 0.8,
        color: NEURAL_COLORS[ci],
        logoColor: LOGO_COLORS[ci],
        x2d: 0, y2d: 0, scale: 1,
        energy: 0,
        ns: [Math.random() * 100, Math.random() * 100, Math.random() * 100],
        phase: Math.random() * Math.PI * 2,
        sensitivity: 0.55 + Math.random() * 0.90,
        atLogoTarget: false,
        logoTarget: null,
      });
    }
    particles.current = np;
  };

  // Sample logo targets: outline particles trace the path, interior particles fill the shape.
  // Interior points are rejection-sampled via Path2D + isPointInPath.
  const sampleLogoPoints = (canvasW, canvasH) => {
    try {
      const svgNS  = 'http://www.w3.org/2000/svg';
      const svgEl  = document.createElementNS(svgNS, 'svg');
      const pathEl = document.createElementNS(svgNS, 'path');
      pathEl.setAttribute('d', LOGO_PATH);
      svgEl.appendChild(pathEl);
      svgEl.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;width:0;height:0;overflow:hidden;';
      document.body.appendChild(svgEl);

      const totalLen     = pathEl.getTotalLength();
      const scale        = Math.min(canvasW * 0.60 / LOGO_SVG_W, canvasH * 0.70 / LOGO_SVG_H);
      const outlineCount = Math.round(PARTICLE_COUNT * 0.62);
      const pts          = [];

      // Outline: uniformly distributed along the path
      for (let i = 0; i < outlineCount; i++) {
        const pt = pathEl.getPointAtLength((i / outlineCount) * totalLen);
        pts.push({
          x: (pt.x - LOGO_SVG_W / 2) * scale,
          y: (pt.y - LOGO_SVG_H / 2) * scale,
        });
      }

      document.body.removeChild(svgEl);

      // Interior: rejection-sample random points inside the filled shape
      const hitCtx  = document.createElement('canvas').getContext('2d');
      const hitPath = new Path2D(LOGO_PATH);
      let attempts  = 0;
      const interiorCount = PARTICLE_COUNT - outlineCount;

      while (pts.length < PARTICLE_COUNT && attempts < interiorCount * 50) {
        attempts++;
        const sx = Math.random() * LOGO_SVG_W;
        const sy = Math.random() * LOGO_SVG_H;
        if (hitCtx.isPointInPath(hitPath, sx, sy)) {
          pts.push({
            x: (sx - LOGO_SVG_W / 2) * scale,
            y: (sy - LOGO_SVG_H / 2) * scale,
          });
        }
      }

      // Safety pad: if rejection sampling stalled, scatter near outline
      while (pts.length < PARTICLE_COUNT) {
        const src = pts[Math.floor(Math.random() * outlineCount)];
        pts.push({ x: src.x + (Math.random() - 0.5) * 6, y: src.y + (Math.random() - 0.5) * 6 });
      }

      logoPointsRef.current = pts;
      logoHitRef.current = { ctx: hitCtx, path: hitPath, scale };

      // Assign targets to particles — outline first, then interior
      const ps = particles.current;
      for (let i = 0; i < ps.length; i++) {
        ps[i].logoTarget  = pts[i] ?? pts[0];
        ps[i].atLogoTarget = false;
      }
    } catch (_) {
      // SVG sampling unavailable; LOGO mode will never trigger
      logoPointsRef.current = null;
    }
  };

  // ── Scene Transitions ───────────────────────────────────────────────────
  const transitionTo = (mode) => {
    const s = state.current;
    // Formation name redirect — route formation keys through FORMATION mode
    if (FORMATIONS[mode]) {
      s.currentFormation = mode;
      s.sceneHistory.push(mode);
      if (s.sceneHistory.length > 6) s.sceneHistory.shift();
      mode = 'FORMATION';
    }
    // Release game roles the moment we leave a game mode; zero strength immediately so
    // formStr drops to 0 and sphereFactor restores — prevents particles freezing at scene positions
    if (s.mode === 'LOGO') {
      s.logoStrength = 0;
    }
    if (s.mode === 'PONG') {
      s.pongStrength = 0;
      const ps = particles.current;
      for (let i = 0; i < ps.length; i++) ps[i].pongRole = null;
    }
    if (s.mode === 'ARKANOID') {
      s.arkanoidStrength = 0;
      const ps = particles.current;
      for (let i = 0; i < ps.length; i++) ps[i].arkRole = null;
    }
    if (s.mode === 'WALLE') {
      s.walleStrength = 0;
      const ps = particles.current;
      for (let i = 0; i < ps.length; i++) ps[i].walleRole = null;
    }
    if (s.mode === 'MONOLITH') {
      s.monolithStrength = 0;
      const ps = particles.current;
      for (let i = 0; i < ps.length; i++) ps[i].monolithRole = null;
    }
    if (s.mode === 'FORMATION') {
      s.formationStrength = 0;
      const ps = particles.current;
      for (let i = 0; i < ps.length; i++) { ps[i].formationTarget = null; ps[i].formationRole = null; }
    }
    if (s.mode === 'MATRIX') {
      s.matrixStrength = 0;
      const ps = particles.current;
      
      // Clear any leftover entry exploding particles immediately to prevent state leaking
      s.explodingGlyphs = [];
      
      // Convert all active canvas trails into lightweight physical particles
      // This ensures the trails don't just 'fade out' but physically become the next form
      s.implodingGlyphs = [];
      if (s.matrix && s.matrix.cols) {
        s.matrix.cols.forEach(col => {
          const cp = ps.find(p => p.matrixColIdx === col.colIdx);
          const colRgb = hexToRgb(cp ? cp.color : '#00FF41');
          
          col.glyphs.forEach((g, idx) => {
            if (!g.isHead) { // Head is already a physical Sentry dot
              const trailPos = idx / col.glyphs.length;
              const alpha = trailPos * col.fadeAlpha;
              const colorStr = `rgba(${colRgb[0]}, ${colRgb[1]}, ${colRgb[2]}, ${alpha})`;
              
              g.particles.forEach(gp => {
                s.implodingGlyphs.push({
                  x: col.x + gp.currX,
                  y: g.yOffset + gp.currY,
                  vx: (Math.random() - 0.5) * 5,
                  vy: (Math.random() - 0.5) * 5,
                  size: gp.size,
                  color: colorStr
                });
              });
            }
          });
        });
        s.matrix.cols = []; // Instantly clear canvas Matrix so they stop drawing in place
      }

      for (let i = 0; i < ps.length; i++) {
        ps[i].matrixRole = null;
      }
    }
    // Track special scene history for anti-repeat
    const SPECIAL = ['LOGO','PONG','ARKANOID','WALLE','MONOLITH','MATRIX'];
    if (SPECIAL.includes(mode)) {
      s.sceneHistory.push(mode);
      if (s.sceneHistory.length > 6) s.sceneHistory.shift();
    }
    s.mode = mode;
    s.transitionAge = 0;
    switch (mode) {
      case 'RESTING':
        s.targetPulseFactor=1.0;  s.targetHomingStr=0.12; s.targetFriction=0.945;
        s.targetSynapseAlpha=0.18; s.targetSynapseMaxDist=100; s.targetRotSpeed=0.003;
        s.targetBoidSep=0.08; s.targetBoidAli=0.04; s.targetBoidCoh=0.02; s.targetBoidRadius=55;
        s.timer = 180 + Math.random() * 80; break;
      case 'SEARCHING':
        s.targetRotX=(Math.random()-0.5)*2.5; s.targetRotY=(Math.random()-0.5)*2.5;
        s.targetPulseFactor=1.04; s.targetHomingStr=0.07; s.targetFriction=0.930;
        s.targetSynapseAlpha=0.24; s.targetSynapseMaxDist=118; s.targetRotSpeed=0.007;
        s.targetBoidSep=0.22; s.targetBoidAli=0.22; s.targetBoidCoh=0.10; s.targetBoidRadius=68;
        s.timer = 220 + Math.random() * 80; break;
      case 'PULSING':
        s.targetHomingStr=0.10; s.targetFriction=0.930;
        s.targetSynapseAlpha=0.34; s.targetSynapseMaxDist=130; s.targetRotSpeed=0.010;
        s.targetBoidSep=0.15; s.targetBoidAli=0.12; s.targetBoidCoh=0.06; s.targetBoidRadius=62;
        s.timer = 200 + Math.random() * 60; break;
      case 'SWARMING':
        s.targetPulseFactor=1.06; s.targetHomingStr=0.035; s.targetFriction=0.920;
        s.targetSynapseAlpha=0.28; s.targetSynapseMaxDist=125; s.targetRotSpeed=0.005;
        s.targetBoidSep=0.40; s.targetBoidAli=0.45; s.targetBoidCoh=0.22; s.targetBoidRadius=78;
        s.timer = 240 + Math.random() * 100; break;
      case 'EXPLORING':
        s.targetPulseFactor=1.0; s.targetHomingStr=0.11; s.targetFriction=0.940;
        s.targetSynapseAlpha=0.20; s.targetSynapseMaxDist=108; s.targetRotSpeed=0.003;
        s.targetBoidSep=0.12; s.targetBoidAli=0.08; s.targetBoidCoh=0.04; s.targetBoidRadius=58;
        s.timer = 230 + Math.random() * 100; break;
      case 'CONTRACTING':
        s.targetPulseFactor=0.52; s.targetHomingStr=0.28; s.targetFriction=0.880;
        s.targetSynapseAlpha=0.42; s.targetSynapseMaxDist=75; s.targetRotSpeed=0.018;
        s.targetBoidSep=0.70; s.targetBoidAli=0.0; s.targetBoidCoh=0.0; s.targetBoidRadius=38;
        s.timer = 95; break;
      case 'BURST':
        waves.current.push({ r: 0, speed: 9, power: 2.5 });
        s.burstPower = 1.0;
        s.targetPulseFactor=1.75; s.targetHomingStr=0.006; s.targetFriction=0.885;
        s.targetSynapseAlpha=0.48; s.targetSynapseMaxDist=190; s.targetRotSpeed=0.024;
        s.targetBoidSep=0.0; s.targetBoidAli=0.0; s.targetBoidCoh=0.0; s.targetBoidRadius=38;
        s.timer = 75; break;
      case 'RECOVERING':
        s.targetPulseFactor=1.0; s.targetHomingStr=0.17; s.targetFriction=0.820;
        s.targetSynapseAlpha=0.18; s.targetSynapseMaxDist=100; s.targetRotSpeed=0.003;
        s.targetBoidSep=0.04; s.targetBoidAli=0.02; s.targetBoidCoh=0.01; s.targetBoidRadius=55;
        s.timer = 999; break;
      case 'LOGO':
        // Flatten rotation, suppress sphere physics; wider synapses fill the interior mesh
        s.targetPulseFactor=1.0; s.targetHomingStr=0.05; s.targetFriction=0.910;
        s.targetSynapseAlpha=0.26; s.targetSynapseMaxDist=105; s.targetRotSpeed=0.0;
        s.targetBoidSep=0.04; s.targetBoidAli=0.02; s.targetBoidCoh=0.01; s.targetBoidRadius=42;
        s.timer = 320 + Math.random() * 180; break;
      case 'PONG': {
        s.targetPulseFactor=1.0; s.targetHomingStr=0.02; s.targetFriction=0.880;
        s.targetSynapseAlpha=0.12; s.targetSynapseMaxDist=32; s.targetRotSpeed=0.0;
        s.targetBoidSep=0.0; s.targetBoidAli=0.0; s.targetBoidCoh=0.0; s.targetBoidRadius=30;
        const pdir = Math.random() > 0.5 ? 1 : -1;
        // Serve at a varied diagonal — random angle 20°–70° from horizontal
        const serveSpd = 5.5 + Math.random() * 2.0;
        const serveAng = (0.22 + Math.random() * 0.56) * Math.PI * 0.5;
        s.pong.bx = 0; s.pong.by = (Math.random() - 0.5) * 60;
        s.pong.bvx = pdir * serveSpd * Math.cos(serveAng);
        s.pong.bvy = (Math.random() > 0.5 ? 1 : -1) * serveSpd * Math.sin(serveAng);
        s.pong.leftY = 0; s.pong.rightY = 0;
        s.pong.leftErrPhase  = Math.random() * Math.PI * 2;
        s.pong.rightErrPhase = Math.random() * Math.PI * 2;
        // Stamp stable roles on particle objects sorted by x-position so the
        // organism visually splits: leftmost → left paddle, rightmost → right paddle
        const ps0 = particles.current;
        const byX = [...ps0].sort((a, b) => a.x - b.x);
        const padN0 = Math.floor(byX.length * 0.28);
        const ballN0 = byX.length - padN0 * 2;
        byX.slice(0, padN0).forEach((p, j) => {
          p.pongRole = 'left';  p.pongIdx = j; p.pongGroupN = padN0;
        });
        byX.slice(padN0, padN0 + ballN0).forEach((p, j) => {
          p.pongRole = 'ball';  p.pongIdx = j; p.pongGroupN = ballN0;
        });
        byX.slice(padN0 + ballN0).forEach((p, j) => {
          p.pongRole = 'right'; p.pongIdx = j; p.pongGroupN = padN0;
        });
        s.timer = 500 + Math.random() * 260; break;
      }
      case 'MATRIX': {
        s.targetPulseFactor = 1.0; s.targetHomingStr = 0.0; s.targetFriction = 0.80;
        s.targetSynapseAlpha = 0.0;
        const cvsM = canvasRef.current;
        const wM = cvsM?.width || 800, hM = cvsM?.height || 600;
        
        const FONT_SIZE = 22;
        const COLUMN_WIDTH = 22;
        
        const getGlyphShape = () => {
          const offsets = [-0.25, 0, 0.25]; 
          const slots = [];
          offsets.forEach(x => offsets.forEach(y => slots.push({ x, y })));
          const count = Math.floor(Math.random() * 4) + 2; 
          return slots.sort(() => 0.5 - Math.random()).slice(0, count);
        };

        const createGlyph = (x, y, isHead) => ({
          yOffset: y,
          isHead,
          lastCharChange: Date.now(),
          particles: getGlyphShape().map(pos => ({
            currX: x + pos.x * FONT_SIZE,
            currY: y + pos.y * FONT_SIZE,
            targetX: pos.x * FONT_SIZE,
            targetY: pos.y * FONT_SIZE,
            vx: 0,
            vy: 0,
            size: 1.1 
          }))
        });

        const numCols = Math.floor(wM / COLUMN_WIDTH) + 1;
        
        s.matrix = {
          getGlyphShape,
          createGlyph,
          cols: Array.from({ length: numCols }, (_, i) => {
            const colY = (Math.random() - 0.5) * hM * 2;
            const colX = (i * COLUMN_WIDTH) - (wM / 2);
            const glyphs = [];
            // Pre-fill trail of 15-28 glyphs extending UPWARDS from colY
            const trailLen = 15 + Math.floor(Math.random() * 10);
            for (let j = 0; j < trailLen; j++) {
               const gy = colY - (trailLen - 1 - j) * FONT_SIZE;
               const g = createGlyph(colX, gy, j === trailLen - 1);
               glyphs.push(g);
            }
            return {
              x: colX,
              y: colY,
              speed: 2 + Math.random() * 5,
              glyphs,
              stopY: 100 + Math.random() * (hM + 8000),
              isFading: false,
              fadeAlpha: 1.0,
              colIdx: i
            };
          })
        };

        const psM = particles.current;
        
        // Organism dissolves into the rain: each particle flies FROM its current organic
        // position TOWARD its assigned matrix column head — no synthetic particles.
        s.explodingGlyphs = [];
        s.matrixTimer = 0; // Matrix canvas renders immediately, fading in with matrixStrength

        waves.current.push({ r: 0, speed: 18, power: 5.5 }); // burst shockwave on entry

        for (let i = 0; i < psM.length; i++) {
          psM[i].matrixRole = 'matrix';
          psM[i].matrixColIdx = i % numCols;
          psM[i].matrixDotIdx = Math.floor(i / numCols);

          // Resolve this particle's matrix head target at transition time
          const col = s.matrix.cols[psM[i].matrixColIdx];
          let targetX = 0, targetY = 0;
          if (col && col.glyphs.length > 0) {
            const headG = col.glyphs[col.glyphs.length - 1];
            if (headG && headG.particles.length > 0) {
              const gp = headG.particles[psM[i].matrixDotIdx % headG.particles.length];
              if (gp) { targetX = col.x + gp.targetX; targetY = headG.yOffset + gp.targetY; }
            }
          }
          // Velocity proportional to distance so every particle arrives in roughly the same
          // number of frames regardless of how far its target is (organic stagger via scatter)
          const dx = targetX - psM[i].x;
          const dy = targetY - psM[i].y;
          const scatter = 0.8 + Math.random() * 0.4;
          psM[i].vx += dx * 0.10 * scatter + (Math.random() - 0.5) * 5;
          psM[i].vy += dy * 0.10 * scatter + (Math.random() - 0.5) * 5;
          psM[i].vz += -psM[i].z * 0.5 + (Math.random() - 0.5) * 3; // flatten to 2D
        }

        // Instantly snap to 2D orientation — the directed burst above masks this.
        s.baseRotX = 0;
        s.rotY = 0;
        s.rotationX = 0;
        s.rotationY = 0;
        
        s.timer = 500 + Math.random() * 200; break;
      }
      case 'FORMATION': {
        s.targetPulseFactor = 1.0; s.targetHomingStr = 0.02; s.targetFriction = 0.920;
        s.targetSynapseAlpha = 0.15; s.targetSynapseMaxDist = 45; s.targetRotSpeed = 0.0;
        s.targetBoidSep = 0.0; s.targetBoidAli = 0.0; s.targetBoidCoh = 0.0; s.targetBoidRadius = 30;
        const fmPts = FORMATIONS[s.currentFormation];
        if (fmPts) {
          const psF = particles.current;
          const byY = [...psF].sort((a, b) => a.y - b.y);
          for (let i = 0; i < psF.length; i++) {
            const pt = fmPts[i % fmPts.length];
            byY[i].formationTarget = { x: pt.x, y: pt.y, z: pt.z || 0 };
            byY[i].formationRole = pt.role;
          }
        }
        s.timer = 400 + Math.random() * 200;
        break;
      }
      case 'FIREWORKS':
        s.targetPulseFactor=2.8; s.targetHomingStr=0.001; s.targetFriction=0.914;
        s.targetSynapseAlpha=0.58; s.targetSynapseMaxDist=230; s.targetRotSpeed=0.06;
        s.targetBoidSep=0.0; s.targetBoidAli=0.0; s.targetBoidCoh=0.0; s.targetBoidRadius=30;
        waves.current.push({ r: 0, speed: 11, power: 4.5 });
        s.burstPower = 2.2;
        s.timer = 185; break;
      case 'ARKANOID': {
        s.targetPulseFactor=1.0; s.targetHomingStr=0.02; s.targetFriction=0.880;
        s.targetSynapseAlpha=0.10; s.targetSynapseMaxDist=28; s.targetRotSpeed=0.0;
        s.targetBoidSep=0.0; s.targetBoidAli=0.0; s.targetBoidCoh=0.0; s.targetBoidRadius=30;
        s.arkanoid.paddleErrPhase = Math.random() * Math.PI * 2;
        s.arkanoid.paddleX = 0;

        // Build brick grid using current canvas dimensions (canvasRef is in scope)
        const canvas0 = canvasRef.current;
        const cw0 = canvas0?.width || 800, ch0 = canvas0?.height || 600;
        const chh0 = ch0 / 2;
        const numCols = 8, numRows = 4;
        const bW = cw0 * 0.80 / numCols;
        const bH = chh0 * 0.46 / numRows;
        const gridLeft = -(numCols / 2) * bW;
        const gridTop  = -chh0 * 0.70;
        const bricks0 = [];
        for (let r = 0; r < numRows; r++) {
          for (let c = 0; c < numCols; c++) {
            bricks0.push({
              alive: true,
              x: gridLeft + (c + 0.5) * bW,
              y: gridTop  + (r + 0.5) * bH,
              hw: bW * 0.42, hh: bH * 0.38,
            });
          }
        }
        s.arkanoid.bricks = bricks0;

        // Ball starts just above paddle, angled upward
        const padY0 = chh0 * 0.72;
        s.arkanoid.bx = 0; s.arkanoid.by = padY0 - 25;
        const sdx = (Math.random() - 0.5) * 1.2;
        const spd0 = 5.5 + Math.random() * 2.0;
        s.arkanoid.bvx = spd0 * sdx;
        s.arkanoid.bvy = -spd0 * Math.sqrt(Math.max(0.1, 1 - sdx * sdx));

        // Assign stable particle roles: bottom → paddle, top → bricks, middle → ball
        const psA = particles.current;
        const padNA = 28, ballNA = 18;
        const brickTot = psA.length - padNA - ballNA;
        const byYA = [...psA].sort((a, b) => b.y - a.y); // bottom-first
        for (let i = 0; i < padNA; i++) {
          byYA[i].arkRole = 'pad'; byYA[i].arkIdx = i; byYA[i].arkGroupN = padNA;
        }
        for (let i = padNA; i < padNA + ballNA; i++) {
          const j = i - padNA;
          byYA[i].arkRole = 'ball'; byYA[i].arkIdx = j; byYA[i].arkGroupN = ballNA;
        }
        // Round-robin so every brick gets particles regardless of count mismatch
        const ppbFinal = Math.ceil(brickTot / bricks0.length);
        for (let i = padNA + ballNA; i < psA.length; i++) {
          const j = i - padNA - ballNA;
          byYA[i].arkRole    = j % bricks0.length;
          byYA[i].arkIdx     = Math.floor(j / bricks0.length);
          byYA[i].arkGroupN  = ppbFinal;
        }

        s.timer = 700 + Math.random() * 400; break;
      }
      case 'WALLE': {
        s.targetPulseFactor=1.0; s.targetHomingStr=0.02; s.targetFriction=0.920;
        s.targetSynapseAlpha=0.10; s.targetSynapseMaxDist=34; s.targetRotSpeed=0.0;
        s.targetBoidSep=0.0; s.targetBoidAli=0.0; s.targetBoidCoh=0.0; s.targetBoidRadius=30;
        s.walle.eyeAngle = 0; s.walle.eyeTarget = 0.20;
        s.walle.eyePause = 60; s.walle.shootActive = false;
        s.walle.shootTimer = 480 + Math.random() * 240;
        const cvs = canvasRef.current;
        const cW = cvs?.width || 800, cH = cvs?.height || 600;
        const chW = cW / 2, chH = cH / 2;
        const sc = Math.min(chW, chH) * 0.006;
        const targets = [];
        // Wall-E positioned right-of-center, looking up-left (matches movie frame)
        const wx = 25 * sc, wy = 20 * sc;

        // ── Tank treads (2 track outlines + wheels) ──
        const treadW = 38, treadH = 14, treadY = wy + 30 * sc;
        const treadPts = [[-treadW,-treadH],[-treadW,treadH],[treadW,treadH],[treadW,-treadH]];
        for (const off of [-18, 18]) {
          const cx = wx + off * sc;
          for (let i = 0; i < 10; i++) {
            const t = i / 9;
            const idx = Math.floor(t * 4) % 4, next = (idx + 1) % 4;
            const lt = (t * 4) % 1;
            const px = treadPts[idx][0] + (treadPts[next][0] - treadPts[idx][0]) * lt;
            const py = treadPts[idx][1] + (treadPts[next][1] - treadPts[idx][1]) * lt;
            targets.push({ x: cx + px * sc * 0.5, y: treadY + py * sc * 0.5, role: 'body' });
          }
          for (let j = 0; j < 4; j++) {
            const a = (j / 4) * Math.PI * 2;
            targets.push({ x: cx + Math.cos(a) * 5 * sc, y: treadY + Math.sin(a) * 5 * sc, role: 'body' });
          }
        }
        // ── Boxy body (outline + panel lines) ──
        const bodyL = wx - 28 * sc, bodyR = wx + 28 * sc;
        const bodyT = wy - 14 * sc, bodyB = wy + 18 * sc;
        const bo = [[bodyL,bodyT],[bodyR,bodyT],[bodyR,bodyB],[bodyL,bodyB]];
        for (let i = 0; i < 24; i++) {
          const t = i / 24, idx = Math.floor(t * 4) % 4, next = (idx + 1) % 4, lt = (t * 4) % 1;
          targets.push({ x: bo[idx][0]+(bo[next][0]-bo[idx][0])*lt, y: bo[idx][1]+(bo[next][1]-bo[idx][1])*lt, role: 'body' });
        }
        for (let i = 0; i < 6; i++) targets.push({ x: bodyL + (bodyR-bodyL)*((i+1)/7), y: (bodyT+bodyB)/2, role: 'body' });
        for (let i = 0; i < 4; i++) targets.push({ x: wx + (i-1.5)*8*sc, y: wy+6*sc, role: 'body' });
        // ── Arms ──
        for (const side of [-1, 1]) {
          const ax = wx + side * 32 * sc, ay = wy - 2 * sc;
          for (let i = 0; i < 4; i++) targets.push({ x: ax+(i%2-0.5)*6*sc, y: ay+(Math.floor(i/2)-0.5)*10*sc, role: 'body' });
        }
        // ── Neck ──
        const neckBot = bodyT, neckTop = wy - 52 * sc;
        for (let i = 0; i < 8; i++) {
          const t = i / 7;
          targets.push({ x: wx + (Math.random()-0.5)*3*sc, y: neckBot+(neckTop-neckBot)*t, role: 'neck' });
        }
        // ── Eyes (binoculars: crossbar + rings + iris + eyelid rims) ──
        const eyeSep = 16 * sc, eyeR = 12 * sc, eyeCY = neckTop;
        for (let i = 0; i < 6; i++) targets.push({ x: wx + ((i/5)-0.5)*eyeSep*2, y: eyeCY, role: 'eyes' });
        for (const sx of [-1, 1]) {
          const ecx = wx + sx * eyeSep;
          for (let i = 0; i < 16; i++) { const a = (i/16)*Math.PI*2; targets.push({ x: ecx+Math.cos(a)*eyeR, y: eyeCY+Math.sin(a)*eyeR, role: 'eyes' }); }
          for (let i = 0; i < 4; i++) { const a = (i/4)*Math.PI*2; targets.push({ x: ecx+Math.cos(a)*eyeR*0.35, y: eyeCY+Math.sin(a)*eyeR*0.35, role: 'eyes' }); }
          for (let i = 0; i < 6; i++) { const a = Math.PI+(i/5)*Math.PI; targets.push({ x: ecx+Math.cos(a)*(eyeR+3*sc), y: eyeCY+Math.sin(a)*(eyeR+3*sc), role: 'eyes' }); }
        }
        // ── Junk pile (diagonal slope, high right where Wall-E sits, low left) ──
        const pileN = 35;
        for (let i = 0; i < pileN; i++) {
          const t = i / (pileN - 1);
          const px = (t * 2.2 - 1.1) * chW * 0.8;
          const slopeY = chH * 0.25 + t * chH * 0.35;
          const jagged = Math.sin(t*19)*8*sc + Math.sin(t*7)*14*sc + Math.sin(t*37)*4*sc;
          targets.push({ x: px, y: slopeY + jagged, role: 'pile' });
          if (i % 2 === 0) targets.push({ x: px + (Math.random()-0.5)*30*sc, y: slopeY + jagged + Math.random()*15*sc, role: 'pile' });
        }
        // ── Stars (fill remaining) ──
        const starCount = Math.max(10, PARTICLE_COUNT - targets.length - 12);
        for (let i = 0; i < starCount; i++) {
          targets.push({ x: (Math.random()-0.55)*chW*1.8, y: -chH*0.15 - Math.random()*chH*0.75, role: 'star' });
        }
        // ── Shooting star ──
        while (targets.length < PARTICLE_COUNT) targets.push({ x: -chW*0.6, y: -chH*0.55, role: 'shoot' });
        // Assign to particles
        const psW = particles.current;
        const byYW = [...psW].sort((a, b) => a.y - b.y);
        for (let i = 0; i < psW.length; i++) {
          const tgt = targets[i] || targets[targets.length - 1];
          byYW[i].walleRole = tgt.role;
          byYW[i].walleTarget = { x: tgt.x, y: tgt.y };
        }
        s.timer = 600 + Math.random() * 200;
        break;
      }
      case 'MONOLITH': {
        s.targetPulseFactor=1.0; s.targetHomingStr=0.02; s.targetFriction=0.920;
        s.targetSynapseAlpha=0.35; s.targetSynapseMaxDist=65; s.targetRotSpeed=0.0;
        s.targetBoidSep=0.0; s.targetBoidAli=0.0; s.targetBoidCoh=0.0; s.targetBoidRadius=30;
        const cvs = canvasRef.current;
        const cW = cvs?.width || 800, cH = cvs?.height || 600;
        const chW = cW / 2, chH = cH / 2;
        const sc = Math.min(chW, chH) * 0.006;
        const targets = [];

        // ── Monolith: proper 1:4 ratio tall rectangular slab (100 particles) ──
        const mW = 27 * sc, mH = 108 * sc;
        const mX = 0, mY = -10 * sc;
        // Dense solid fill
        for (let i = 0; i < 60; i++) {
          targets.push({
            x: mX + (Math.random() - 0.5) * mW * 1.85,
            y: mY + (Math.random() - 0.5) * mH * 1.85,
            role: 'monolith'
          });
        }
        // Sharp perimeter edges
        for (let i = 0; i < 16; i++) {
          const t = i / 15;
          targets.push({ x: mX - mW, y: mY - mH + t * 2 * mH, role: 'monolith' });
          targets.push({ x: mX + mW, y: mY - mH + t * 2 * mH, role: 'monolith' });
        }
        for (let i = 0; i < 5; i++) {
          const t = (i + 1) / 6;
          targets.push({ x: mX - mW + t * 2 * mW, y: mY - mH, role: 'monolith' });
          targets.push({ x: mX - mW + t * 2 * mW, y: mY + mH, role: 'monolith' });
        }
        // = 60 + 32 + 10 = 102 monolith particles

        // ── Monkey (29 particles) ──
        const msc = sc * 1.5;
        const armLen = 22 * msc;
        const monkeyX = -78 * sc, monkeyY = 42 * sc;
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          targets.push({ x: monkeyX + Math.cos(a)*4*msc + (Math.cos(a)>0?2.5*msc:0), y: monkeyY - 24*msc + Math.sin(a)*4*msc, role: 'monkey' });
        }
        for (let i = 0; i < 8; i++) {
          targets.push({ x: monkeyX + Math.sin((i/7)*Math.PI)*8*msc - 3*msc, y: monkeyY - 18*msc + (i/7)*24*msc, role: 'monkey' });
        }
        for (let i = 0; i < 4; i++) {
          const t = i / 3;
          targets.push({ x: monkeyX - 6*msc - t*armLen*0.7, y: monkeyY - 15*msc - t*armLen, role: 'monkey' });
          targets.push({ x: monkeyX + 6*msc + t*armLen*0.7, y: monkeyY - 15*msc - t*armLen, role: 'monkey' });
        }
        for (let i = 0; i < 4; i++) {
          targets.push({ x: monkeyX - 4*msc - (i/3)*10*msc, y: monkeyY + 6*msc + (i/3)*8*msc, role: 'monkey' });
          targets.push({ x: monkeyX + 4*msc + (i/3)*10*msc, y: monkeyY + 6*msc + (i/3)*8*msc, role: 'monkey' });
        }

        // ── Stick / bone that the monkey throws (7 particles) ──
        const stickCX = monkeyX + 6*msc + armLen*0.7;
        const stickCY = monkeyY - 15*msc - armLen;
        for (let i = 0; i < 7; i++) {
          const f = (i / 6) - 0.5;
          targets.push({ x: stickCX + f * 30 * sc, y: stickCY, role: 'stick', stickFraction: f });
        }
        s.monolith = {
          throwTimer: 280 + Math.random() * 160,
          throwPhase: 'rest',
          stickCenterX: stickCX,
          stickCenterY: stickCY,
          stickRestY: stickCY,
          stickVy: 0,
          stickAngle: 0,
          stickAngleVel: 0.028,
        };

        // ── Bonfire (25 particles) ──
        const bx = 68*sc, by = 65*sc;
        for (let i = 0; i < 5; i++) {
          targets.push({ x: bx - 10*sc + (i/4)*20*sc, y: by + 6*sc, role: 'rocks' });
        }
        for (let i = 0; i < 20; i++) {
          targets.push({ x: bx + (Math.random()-0.5)*10*sc, y: by, role: 'fire' });
        }

        // ── Rocky Field (14 particles) ──
        const floorY = 80 * sc;
        for (let i = 0; i < 14; i++) {
          const rx = (i / 13 * 2.2 - 1.1) * chW;
          const ry = floorY + (Math.random()-0.5)*15*sc;
          targets.push({ x: rx, y: ry, role: 'rocks' });
        }

        // ── Stars ──
        while (targets.length < PARTICLE_COUNT) {
          targets.push({ x: (Math.random()-0.5)*chW*2.2, y: -chH*0.4 - Math.random()*chH*0.7, role: 'star' });
        }

        const psM = particles.current;
        const byYM = [...psM].sort((a, b) => a.y - b.y);
        for (let i = 0; i < psM.length; i++) {
          const tgt = targets[i] || targets[targets.length - 1];
          byYM[i].monolithRole = tgt.role;
          byYM[i].monolithTarget = { x: tgt.x, y: tgt.y };
          if (tgt.stickFraction !== undefined) byYM[i].stickFraction = tgt.stickFraction;
        }
        s.timer = 650 + Math.random() * 250;
        break;
      }
      default: break;
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const update = () => {
      const p = canvas.parentElement;
      if (!p) return;
      canvas.width  = p.clientWidth;
      canvas.height = p.clientHeight;
      initOrganism();
      sampleLogoPoints(canvas.width, canvas.height);
    };
    const ro = new ResizeObserver(update);
    if (canvas.parentElement) ro.observe(canvas.parentElement);
    update();
    return () => ro.disconnect();
  }, []); // eslint-disable-line

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animId = null;

    const render = () => {
      animId = requestAnimationFrame(render);
      const s  = state.current;
      const m  = mouse.current;
      const ps = particles.current;
      const w  = canvas.width, h = canvas.height;
      const hw = w / 2, hh = h / 2;
      const af = ++frame.current;

      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      if (s.matrixStrength > 0.01) {
        ctx.shadowBlur = 0;
        // Smoothly interpolate background alpha to ensure trails fade cleanly on exit
        // and background remains native #1c1c1c (rgb 28) instead of pure black
        const bgAlpha = Math.min(1.0, 0.12 + (1 - s.matrixStrength) * 1.5);
        ctx.fillStyle = `rgba(28, 28, 28, ${bgAlpha})`; 
      } else {
        ctx.fillStyle = '#1c1c1c';
      }
      ctx.fillRect(0, 0, w, h);

      // ── Process Imploding Glyphs (from exiting Matrix) ────────────────────
      if (s.implodingGlyphs?.length > 0) {
        for (let i = s.implodingGlyphs.length - 1; i >= 0; i--) {
          const ig = s.implodingGlyphs[i];
          ig.x += (0 - ig.x) * 0.15;
          ig.y += (0 - ig.y) * 0.15;
          const dist = Math.sqrt(ig.x*ig.x + ig.y*ig.y) || 1;
          const gx = hw + ig.x, gy = hh + ig.y;
          ctx.beginPath();
          ctx.arc(gx, gy, ig.size, 0, Math.PI * 2);
          ctx.fillStyle = ig.color;
          ctx.fill();
          if (dist < 10) s.implodingGlyphs.splice(i, 1);
        }
      }

      // ── Process Exploding Glyphs (from entering Matrix) ───────────────────
      if (s.explodingGlyphs && s.explodingGlyphs.length > 0 && s.mode === 'MATRIX') {
         const mStr = Math.max(0, s.matrixStrength);
         for (let i = s.explodingGlyphs.length - 1; i >= 0; i--) {
            const eg = s.explodingGlyphs[i];
            
            // Fast lerp directly from center (0,0) to target, exact mathematical inverse of implosion
            eg.x += (eg.tx - eg.x) * 0.15;
            eg.y += (eg.ty - eg.y) * 0.15;
            
            // Blend from Sentry color to Matrix green
            const r = Math.round(eg.cr + (0 - eg.cr) * mStr);
            const g = Math.round(eg.cg + (255 - eg.cg) * mStr);
            const b = Math.round(eg.cb + (65 - eg.cb) * mStr);
            
            const gx = hw + eg.x;
            const gy = hh + eg.y;
            ctx.beginPath();
            ctx.arc(gx, gy, eg.size, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${eg.alpha * mStr})`;
            ctx.fill();
         }
      }

      ctx.globalCompositeOperation = 'lighter';

      // Lerp all physics params — ease in slowly after a mode change for seamless transitions
      s.transitionAge++;
      const T = Math.min(0.035, 0.010 + s.transitionAge * 0.0004);
      s.pulseFactor    = lerp(s.pulseFactor,    s.targetPulseFactor,    T);
      s.homingStr      = lerp(s.homingStr,       s.targetHomingStr,      T);
      s.friction       = lerp(s.friction,        s.targetFriction,       T);
      s.synapseAlpha   = lerp(s.synapseAlpha,    s.targetSynapseAlpha,   T);
      s.synapseMaxDist = lerp(s.synapseMaxDist,  s.targetSynapseMaxDist, T);
      s.rotSpeed       = lerp(s.rotSpeed,        s.targetRotSpeed,       T);
      s.boidSep        = lerp(s.boidSep,         s.targetBoidSep,        T * 1.5);
      s.boidAli        = lerp(s.boidAli,         s.targetBoidAli,        T * 1.5);
      s.boidCoh        = lerp(s.boidCoh,         s.targetBoidCoh,        T * 1.5);
      s.boidRadius     = lerp(s.boidRadius,      s.targetBoidRadius,     T);

      // Logo strength: blend smoothly toward 1 in LOGO mode, 0 otherwise
      const logoTarget = s.mode === 'LOGO' ? 1 : 0;
      s.logoStrength = lerp(s.logoStrength, logoTarget, s.mode === 'LOGO' ? 0.012 : 0.020);

      // Pong strength
      const pongTarget = s.mode === 'PONG' ? 1 : 0;
      s.pongStrength = lerp(s.pongStrength, pongTarget, s.mode === 'PONG' ? 0.012 : 0.055);

      // Arkanoid strength
      const arkTarget = s.mode === 'ARKANOID' ? 1 : 0;
      s.arkanoidStrength = lerp(s.arkanoidStrength, arkTarget, s.mode === 'ARKANOID' ? 0.012 : 0.055);

      // Wall-E strength
      const walleTarget = s.mode === 'WALLE' ? 1 : 0;
      s.walleStrength = lerp(s.walleStrength, walleTarget, s.mode === 'WALLE' ? 0.012 : 0.055);

      // Monolith strength
      const monoTarget = s.mode === 'MONOLITH' ? 1 : 0;
      s.monolithStrength = lerp(s.monolithStrength, monoTarget, s.mode === 'MONOLITH' ? 0.012 : 0.055);

      // Matrix strength
      const matrixTarget = s.mode === 'MATRIX' ? 1 : 0;
      s.matrixStrength = lerp(s.matrixStrength, matrixTarget, s.mode === 'MATRIX' ? 0.010 : 0.045);

      // Formation strength
      const formationTarget = s.mode === 'FORMATION' ? 1 : 0;
      s.formationStrength = lerp(s.formationStrength, formationTarget, s.mode === 'FORMATION' ? 0.012 : 0.055);

      if (!m.active && m.leaveTimer > 0) {
        m.leaveTimer--;
        if (m.leaveTimer <= 0) transitionTo('RECOVERING');
      }

      if (!m.active) {
        s.timer--;
        if (s.mode === 'RECOVERING') {
          s.rotationX *= 0.96;
          let maxSpd = 0;
          for (let i = 0; i < ps.length; i++) {
            const p = ps[i];
            const spd = p.vx*p.vx + p.vy*p.vy + p.vz*p.vz;
            if (spd > maxSpd) maxSpd = spd;
          }
          if (maxSpd < 0.25) transitionTo('RESTING');
        } else if (s.mode === 'CONTRACTING' && s.timer <= 0) {
          transitionTo('BURST');
        } else if (s.mode === 'BURST' && s.timer <= 0) {
          transitionTo('SWARMING');
        } else if (s.mode === 'LOGO' && s.timer <= 0) {
          transitionTo('RESTING');
        } else if (s.mode === 'PONG' && s.timer <= 0) {
          transitionTo('CONTRACTING');
        } else if (s.mode === 'FIREWORKS' && s.timer <= 0) {
          transitionTo('RECOVERING');
        } else if (s.mode === 'ARKANOID' && s.timer <= 0) {
          transitionTo('CONTRACTING');
        } else if (s.mode === 'WALLE' && s.timer <= 0) {
          transitionTo('CONTRACTING');
        } else if (s.mode === 'FORMATION' && s.timer <= 0) {
          transitionTo('CONTRACTING');
        } else if (s.timer <= 0 && s.mode !== 'RECOVERING') {
          // Mood-weighted scene selection
          const md = s.mood;
          const allSpecial = [
            logoPointsRef.current ? 'LOGO' : null,
            'PONG', 'ARKANOID', 'WALLE', 'MONOLITH', 'MATRIX',
            'DNA_HELIX', 'EKG', 'ATOM', 'BINARY_TREE',
          ].filter(Boolean);
          const available = allSpecial.filter(sc => !s.sceneHistory.includes(sc));
          const maxDrive = Math.max(md.curiosity, md.restlessness, md.playfulness);
          if (available.length > 0 && Math.random() < (0.18 + maxDrive * 0.32)) {
            const scores = available.map(sc => {
              const aff = SCENE_AFFINITIES[sc] || { curiosity: 0.3, restlessness: 0.3, playfulness: 0.3 };
              return md.curiosity * aff.curiosity + md.restlessness * aff.restlessness + md.playfulness * aff.playfulness;
            });
            const maxSc = Math.max(...scores);
            const expSc = scores.map(v => Math.exp((v - maxSc) * 3.5));
            const sumSc = expSc.reduce((a, b) => a + b, 0);
            const probs = expSc.map(v => v / sumSc);
            let rnd = Math.random(), picked = available[available.length - 1];
            for (let si = 0; si < available.length; si++) {
              rnd -= probs[si];
              if (rnd <= 0) { picked = available[si]; break; }
            }
            md.curiosity    = Math.max(0, md.curiosity    - 0.55);
            md.restlessness = Math.max(0, md.restlessness - 0.55);
            md.playfulness  = Math.max(0, md.playfulness  - 0.55);
            transitionTo(picked);
          } else {
            const ge = s.globalEnergy;
            let pool = ge > 0.42
              ? ['CONTRACTING','CONTRACTING','SWARMING','SWARMING','PULSING','PULSING','SEARCHING','EXPLORING','EXPLORING']
              : ['CONTRACTING','SWARMING','PULSING','SEARCHING','EXPLORING','RESTING','RESTING','RESTING'];
            pool = pool.filter(sc => sc !== s.mode);
            transitionTo(pool[Math.floor(Math.random() * pool.length)]);
          }
        }
      }

      // Mood drift: drives accumulate slowly, vigilance spikes on hover and decays otherwise
      if (m.active) {
        s.mood.vigilance    = Math.min(1, s.mood.vigilance    + 0.012);
        s.mood.restlessness = Math.min(1, s.mood.restlessness + 0.003);
      } else {
        s.mood.curiosity    = Math.min(1, s.mood.curiosity    + 0.00018);
        s.mood.restlessness = Math.min(1, s.mood.restlessness + 0.00012);
        s.mood.playfulness  = Math.min(1, s.mood.playfulness  + 0.00010);
        s.mood.vigilance    = Math.max(0, s.mood.vigilance    - 0.0050);
      }

      if (!m.active) {
        if (s.mode === 'LOGO' || s.mode === 'PONG' || s.mode === 'ARKANOID' || s.mode === 'WALLE' || s.mode === 'MONOLITH' || s.mode === 'MATRIX' || s.mode === 'FORMATION') {
          // Flatten rotation so logo/game/scene renders face-on
          s.rotationX = lerp(s.rotationX, 0, 0.025);
          s.rotationY = lerp(s.rotationY, 0, 0.018);
        } else if (s.mode === 'SEARCHING') {
          s.rotationX += (s.targetRotX - s.rotationX) * 0.010;
          s.rotationY += (s.targetRotY - s.rotationY) * 0.010;
          s.rotationY += s.rotSpeed;
        } else if (s.mode !== 'RECOVERING') {
          s.rotationX *= 0.985;
          s.rotationY += s.rotSpeed;
        }
      }

      // Double-heartbeat rhythm for PULSING
      if (s.mode === 'PULSING' && !m.active) {
        const beat = (af % 110) / 110;
        const pulse = beat < 0.09 ? Math.sin(beat / 0.09 * Math.PI) :
                      beat < 0.22 ? Math.sin((beat - 0.13) / 0.09 * Math.PI) * 0.70 : 0;
        s.targetPulseFactor = 1.0 + pulse * 0.58;
      }

      // Advance + prune shockwaves
      const wv = waves.current;
      for (let wi = wv.length - 1; wi >= 0; wi--) {
        wv[wi].r += wv[wi].speed;
        if (wv[wi].r > BASE_RADIUS * 2.8) wv.splice(wi, 1);
      }

      // Burst radial impulse
      if (s.mode === 'BURST' && s.burstPower > 0.01 && !m.active) {
        const mag = s.burstPower * 24;
        for (let i = 0; i < ps.length; i++) {
          const p = ps[i];
          const len = Math.sqrt(p.x*p.x + p.y*p.y + p.z*p.z) || 1;
          p.vx += (p.x / len) * mag;
          p.vy += (p.y / len) * mag;
          p.vz += (p.z / len) * mag;
          p.energy = 1;
        }
        s.burstPower = lerp(s.burstPower, 0, 0.22);
      }

      // Fireworks: sustained multi-burst celebration for 3 seconds
      if (s.mode === 'FIREWORKS') {
        // Re-fire burst wave every ~52 frames
        if (af % 52 === 0) {
          waves.current.push({ r: 0, speed: 13, power: 3.2 });
          s.burstPower = Math.max(s.burstPower, 1.2);
        }
        // Random particle launches — each lit up in full energy
        if (Math.random() < 0.12) {
          const pj  = ps[Math.floor(Math.random() * ps.length)];
          const ang = Math.random() * Math.PI * 2;
          const str = 10 + Math.random() * 14;
          pj.vx += Math.cos(ang) * str;
          pj.vy += Math.sin(ang) * str;
          pj.energy = 1;
        }
      }

      const cosY = Math.cos(s.rotationY), sinY = Math.sin(s.rotationY);
      const cosX = Math.cos(s.rotationX), sinX = Math.sin(s.rotationX);

      // Bubble deformation centers: wander across sphere surface
      const dcs = s.deformCenters;
      for (let di = 0; di < dcs.length; di++) {
        const dc = dcs[di];
        dc.theta += dc.dTheta;
        dc.phi   += dc.dPhi;
        if (dc.phi >  Math.PI * 0.48) { dc.phi =  Math.PI * 0.48; dc.dPhi = -Math.abs(dc.dPhi); }
        if (dc.phi < -Math.PI * 0.48) { dc.phi = -Math.PI * 0.48; dc.dPhi =  Math.abs(dc.dPhi); }
        const cp = Math.cos(dc.phi);
        dc.cx = cp * Math.cos(dc.theta);
        dc.cy = Math.sin(dc.phi);
        dc.cz = cp * Math.sin(dc.theta);
        dc.curAmp = dc.amp * Math.sin(af * dc.ampFreq + dc.ampPhase);
      }

      // Shoal drift: organism wanders like a school of fish in all modes, always returning to center
      if (!m.active && s.mode !== 'LOGO' && s.mode !== 'PONG' && s.mode !== 'ARKANOID' && s.mode !== 'WALLE' && s.mode !== 'FORMATION') {
        const sh = s.shoal;
        sh.timer--;
        if (sh.timer <= 0) {
          if (!sh.returning) {
            // Pick a new random drift destination
            const angle = Math.random() * Math.PI * 2;
            const amp   = s.mode === 'EXPLORING' ? 72 : 46;
            const dist  = amp * (0.4 + Math.random() * 0.6);
            sh.tx = Math.cos(angle) * dist;
            sh.ty = Math.sin(angle) * dist * 0.72;
            sh.tz = (Math.random() - 0.5) * 22;
            sh.returning = true;
            sh.timer = 220 + Math.random() * 140;
          } else {
            // Return to center
            sh.tx = 0; sh.ty = 0; sh.tz = 0;
            sh.returning = false;
            sh.timer = 160 + Math.random() * 100;
          }
        }
        const sp = s.mode === 'EXPLORING' ? 0.0045 : 0.0030;
        sh.vx += (sh.tx - sh.x) * sp;
        sh.vy += (sh.ty - sh.y) * sp;
        sh.vz += (sh.tz - sh.z) * sp * 0.55;
        sh.vx *= 0.963; sh.vy *= 0.963; sh.vz *= 0.970;
        sh.x  += sh.vx;  sh.y  += sh.vy;  sh.z  += sh.vz;
      } else if (s.mode === 'LOGO' || s.mode === 'PONG' || s.mode === 'ARKANOID' || s.mode === 'WALLE' || s.mode === 'MONOLITH' || s.mode === 'FORMATION') {
        // Drift back to center while forming logo/pong/arkanoid/walle/monolith/formation
        const sh = s.shoal;
        sh.tx = 0; sh.ty = 0; sh.tz = 0;
        sh.vx *= 0.92; sh.vy *= 0.92; sh.vz *= 0.92;
        sh.x  += sh.vx; sh.y  += sh.vy; sh.z  += sh.vz;
      }

      // Pre-compute scene strength composite (needed by living systems and particle loop)
      const lStr    = s.logoStrength;
      const pStr    = s.pongStrength;
      const arkStr  = s.arkanoidStrength;
      const walleStr = s.walleStrength;
      const monoStr = s.monolithStrength;
      const frmStr  = s.formationStrength;
      const formStr = Math.max(lStr, pStr, arkStr, walleStr, monoStr, frmStr);

      // ── Pseudopod lifecycle: organic surface extrusions ─────────────────
      if (formStr < 0.15) {
        const pods = s.pseudopods;
        s.pseudopodTimer--;
        if (s.pseudopodTimer <= 0 && pods.length < 2) {
          const theta0 = Math.random() * Math.PI * 2;
          const phi0   = (Math.random() - 0.5) * Math.PI;
          const cp0    = Math.cos(phi0);
          pods.push({
            nx: cp0 * Math.cos(theta0), ny: Math.sin(phi0), nz: cp0 * Math.sin(theta0),
            strength: 0, targetStrength: 0.65 + Math.random() * 0.5,
            phase: 'grow', timer: 90 + Math.random() * 90,
            cone: 0.48 + Math.random() * 0.22,
          });
          s.pseudopodTimer = 230 + Math.random() * 270;
        }
        for (let pi = pods.length - 1; pi >= 0; pi--) {
          const pod = pods[pi];
          pod.strength = lerp(pod.strength, pod.phase === 'retract' ? 0 : pod.targetStrength, 0.028);
          pod.timer--;
          if (pod.phase === 'grow' && pod.timer <= 0)  { pod.phase = 'hold'; pod.timer = 55 + Math.random() * 55; }
          else if (pod.phase === 'hold' && pod.timer <= 0) { pod.phase = 'retract'; }
          if (pod.phase === 'retract' && pod.strength < 0.03) pods.splice(pi, 1);
        }
      } else if (s.pseudopods.length > 0) {
        s.pseudopods = [];
      }

      // ── Blob morph: autonomous shape deformation ──────────────────────────
      {
        const bm = s.blobMorph;
        const canMorph = !m.active && formStr < 0.1 &&
          (s.mode === 'RESTING' || s.mode === 'EXPLORING' || s.mode === 'SWARMING' || s.mode === 'PULSING');
        bm.strength = lerp(bm.strength, canMorph ? bm.targetStrength : 0, 0.007);
        if (bm.mode !== 'none') {
          bm.phase += 0.016;
          if (canMorph) bm.timer--;
          if (bm.timer <= 0 || !canMorph) {
            bm.targetStrength = 0;
            if (bm.strength < 0.04) bm.mode = 'none';
          }
        }
        if (canMorph && bm.mode === 'none' && Math.random() < 0.0018) {
          const morphModes = ['elongate', 'flatten', 'lobe', 'spin'];
          bm.mode = morphModes[Math.floor(Math.random() * morphModes.length)];
          const th2 = Math.random() * Math.PI * 2;
          const ph2 = (Math.random() - 0.5) * Math.PI;
          const cp2 = Math.cos(ph2);
          bm.axis = { x: cp2 * Math.cos(th2), y: Math.sin(ph2), z: cp2 * Math.sin(th2) };
          bm.targetStrength = 0.55 + Math.random() * 0.45;
          bm.phase = 0;
          bm.timer = 190 + Math.random() * 260;
        }
      }

      // ── Hover lure: prolonged still hover draws organism toward cursor ────
      if (m.active && m.speed < 2.0 && formStr < 0.1) {
        s.hoverLureTimer = Math.min(220, s.hoverLureTimer + 1);
      } else {
        s.hoverLureTimer = Math.max(0, s.hoverLureTimer - 2);
      }
      if (s.hoverLureTimer > 80 && m.active && formStr < 0.1) {
        const lureStr = Math.min(1, (s.hoverLureTimer - 80) / 120) * 0.0007;
        const sh = s.shoal;
        sh.vx += (m.x * 0.32 - sh.x) * lureStr;
        sh.vy += (m.y * 0.32 - sh.y) * lureStr;
      }
      if (!m.active) m.angularVel *= 0.92;

      const boidRadSq = s.boidRadius * s.boidRadius;
      const sepRadSq  = (s.boidRadius * 0.32) ** 2;
      const doBoids   = s.boidSep > 0.005 || s.boidAli > 0.005 || s.boidCoh > 0.005;
      let sumEnergy = 0;

      // ── PONG ball physics ──────────────────────────────────────────────────
      if (pStr > 0.05) {
        const pg    = s.pong;
        const pongW = hw * 0.56;
        const pongH = hh * 0.70;
        const pHH   = pongH * 0.36;
        const ballR = 20;

        pg.bx += pg.bvx * pStr;
        pg.by += pg.bvy * pStr;

        // Slight air friction between hits — ball decelerates naturally
        pg.bvx *= 0.9992;
        pg.bvy *= 0.9992;

        // Top / bottom bounce — account for ballR and push out slightly
        if (Math.abs(pg.by) > pongH - ballR) {
          pg.bvy = -pg.bvy * (1.0 + Math.random() * 0.04);
          pg.bvx += (Math.random() - 0.5) * 0.8;
          pg.by  = Math.sign(pg.by) * (pongH - ballR);
        }

        // Left paddle hit — speed boost + angle from hit position
        if (pg.bvx < 0 && pg.bx < -pongW + ballR && Math.abs(pg.by - pg.leftY) < pHH + ballR) {
          const hitFrac = (pg.by - pg.leftY) / pHH;
          const inSpd = Math.sqrt(pg.bvx ** 2 + pg.bvy ** 2);
          const outSpd = Math.max(5, inSpd * (1.12 + Math.random() * 0.06));
          const outAng = hitFrac * 1.1 + (Math.random() - 0.5) * 0.3;
          pg.bvx = outSpd * Math.cos(outAng);
          pg.bvy = outSpd * Math.sin(outAng);
          if (pg.bvx < 1) pg.bvx = 1; // ensure it moves right
          pg.bx  = -pongW + ballR;
        }

        // Right paddle hit
        if (pg.bvx > 0 && pg.bx > pongW - ballR && Math.abs(pg.by - pg.rightY) < pHH + ballR) {
          const hitFrac = (pg.by - pg.rightY) / pHH;
          const inSpd = Math.sqrt(pg.bvx ** 2 + pg.bvy ** 2);
          const outSpd = Math.max(5, inSpd * (1.12 + Math.random() * 0.06));
          const outAng = Math.PI - hitFrac * 1.1 + (Math.random() - 0.5) * 0.3;
          pg.bvx = outSpd * Math.cos(outAng);
          pg.bvy = outSpd * Math.sin(outAng);
          if (pg.bvx > -1) pg.bvx = -1; // ensure it moves left
          pg.bx  = pongW - ballR;
        }

        // Ball escaped — game over
        if (Math.abs(pg.bx) > pongW * 1.25) {
          transitionTo('CONTRACTING');
        }

        // Occasional diagonal kick — keeps auto mode unpredictable
        if (Math.random() < 0.004) {
          const spd3 = Math.sqrt(pg.bvx ** 2 + pg.bvy ** 2);
          const ang3 = Math.atan2(pg.bvy, pg.bvx) + (Math.random() - 0.5) * 0.9;
          pg.bvx = Math.cos(ang3) * spd3 * 1.08;
          pg.bvy = Math.sin(ang3) * spd3 * 1.08;
        }

        // Speed clamp
        const spd = Math.sqrt(pg.bvx ** 2 + pg.bvy ** 2);
        if (spd > 13) { pg.bvx = (pg.bvx / spd) * 13; pg.bvy = (pg.bvy / spd) * 13; }

        // AI paddle tracking with a smoother oscillation — reduces jittery bounciness
        pg.leftErrPhase  += 0.012; // slowed from 0.019
        pg.rightErrPhase += 0.015; // slowed from 0.023
        const leftErr  = Math.sin(pg.leftErrPhase)  * 18; // reduced from 26
        const rightErr = Math.sin(pg.rightErrPhase) * 20; // reduced from 30

        pg.leftY += (pg.by + leftErr - pg.leftY) * 0.085; // more responsive
        if (m.active && pStr > 0.5) {
          // User controls right paddle directly
          pg.rightY = Math.max(-pongH + pHH, Math.min(pongH - pHH, m.y));
        } else {
          pg.rightY += (pg.by + rightErr - pg.rightY) * 0.085;
        }
      }

      // ── ARKANOID ball / paddle / brick physics ──────────────────────────────
      if (arkStr > 0.05 && s.arkanoid.bricks) {
        const ak   = s.arkanoid;
        const padW = w  * 0.10;   // half-paddle-width
        const padY = hh * 0.72;   // paddle y (canvas-relative)
        const ballR = 16;

        ak.bx += ak.bvx * arkStr;
        ak.by += ak.bvy * arkStr;

        // Air friction — slight deceleration between hits
        ak.bvx *= 0.9994;
        ak.bvy *= 0.9994;

        // Side wall bounces — account for ballR
        if (Math.abs(ak.bx) > hw * 0.90 - ballR) {
          ak.bvx = -ak.bvx * (1.0 + Math.random() * 0.04);
          ak.bvx += (Math.random() - 0.5) * 0.5;
          ak.bx = Math.sign(ak.bx) * (hw * 0.90 - ballR);
        }

        // Top wall bounce — account for ballR
        if (ak.by < -hh * 0.88 + ballR) {
          ak.bvy = Math.abs(ak.bvy) * (1.0 + Math.random() * 0.04);
          ak.bvx += (Math.random() - 0.5) * 0.6;
          ak.by = -hh * 0.88 + ballR;
        }

        // Paddle collision — speed boost + angle from hit position
        if (ak.bvy > 0 && ak.by > padY - ballR && ak.by < padY + ballR * 2 &&
            Math.abs(ak.bx - ak.paddleX) < padW + ballR) {
          const hitFrac = (ak.bx - ak.paddleX) / padW;
          const inSpd   = Math.sqrt(ak.bvx ** 2 + ak.bvy ** 2);
          const outSpd  = Math.max(5.5, inSpd * (1.15 + Math.random() * 0.08));
          const outAng  = -Math.PI / 2 + hitFrac * 1.2 + (Math.random() - 0.5) * 0.2;
          ak.bvx = outSpd * Math.cos(outAng);
          ak.bvy = outSpd * Math.sin(outAng);
          if (ak.bvy > -2) ak.bvy = -2; // ensure it moves up
          ak.by  = padY - ballR;
        }

        // Brick collisions (AABB, resolve shortest axis)
        const bks = ak.bricks;
        for (let bi = 0; bi < bks.length; bi++) {
          const brick = bks[bi];
          if (!brick.alive) continue;
          const dx   = ak.bx - brick.x;
          const dy   = ak.by - brick.y;
          const overX = brick.hw + ballR - Math.abs(dx);
          const overY = brick.hh + ballR - Math.abs(dy);
          if (overX > 0 && overY > 0) {
            if (overX < overY) {
              ak.bvx = Math.sign(dx) * Math.abs(ak.bvx);
              ak.bx += Math.sign(dx) * overX * 0.5;
            } else {
              ak.bvy = Math.sign(dy) * Math.abs(ak.bvy);
              ak.by += Math.sign(dy) * overY * 0.5;
            }
            ak.bvx += (Math.random() - 0.5) * 0.6;
            brick.alive = false;
            // Give the brick's particles a scatter impulse then release them
            for (let pi = 0; pi < ps.length; pi++) {
              if (ps[pi].arkRole === bi) {
                ps[pi].vx += ak.bvx * 0.35 + (Math.random() - 0.5) * 4;
                ps[pi].vy += Math.abs(ak.bvy) * 0.4 + Math.random() * 3;
                ps[pi].energy = Math.min(1, ps[pi].energy + 0.9);
                ps[pi].arkRole = null;
              }
            }
            break; // one brick per frame
          }
        }

        // Ball lost — game over immediately
        if (ak.by > padY + hh * 0.18) {
          transitionTo('CONTRACTING');
        }

        // All bricks cleared without losing → fireworks celebration
        if (bks.every(b => !b.alive)) transitionTo('FIREWORKS');

        // Speed clamp
        const akSpd = Math.sqrt(ak.bvx ** 2 + ak.bvy ** 2);
        if (akSpd > 12) { ak.bvx = (ak.bvx / akSpd) * 12; ak.bvy = (ak.bvy / akSpd) * 12; }

        // Paddle: mouse (x-axis) or AI tracking ball with smoother movement
        ak.paddleErrPhase += 0.012; // slowed from 0.016
        const padErr = Math.sin(ak.paddleErrPhase) * 20; // reduced from 34
        if (m.active && arkStr > 0.5) {
          ak.paddleX = Math.max(-hw * 0.82 + padW, Math.min(hw * 0.82 - padW, m.x));
        } else {
          ak.paddleX += (ak.bx + padErr - ak.paddleX) * 0.14; // more responsive
          ak.paddleX  = Math.max(-hw * 0.82 + padW, Math.min(hw * 0.82 - padW, ak.paddleX));
        }
      }

      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        const t = af * 0.007;

        // Multi-frequency organic breathing (suppressed while in logo formation)
        const breatheRaw = Math.sin(af * 0.022 + p.phase) * 5.5
                         + Math.sin(af * 0.009 + p.phase * 1.7) * 2.8
                         + Math.sin(af * 0.047 + p.phase * 0.4) * 1.2;
        const breathe = breatheRaw * (1 - formStr);

        // Bubble surface deformation (suppressed while in logo/pong formation)
        let bubbleDeform = 0;
        if (formStr < 0.98) {
          for (let di = 0; di < dcs.length; di++) {
            const dc  = dcs[di];
            const dot = p.cosP*p.cosT*dc.cx + p.sinP*dc.cy + p.cosP*p.sinT*dc.cz;
            if (dot > 0.25) {
              const tf = (dot - 0.25) / 0.75;
              bubbleDeform += tf * tf * dc.curAmp * (1 - formStr);
            }
          }
        }

        // Shell homing toward origin + drift (blended out as logo/pong activates)
        const sphereFactor = 1 - formStr * 0.96;
        const targetR = (p.originRadius + breathe + bubbleDeform) * s.pulseFactor;
        const ox = p.ox + s.shoal.x, oy = p.oy + s.shoal.y, oz = p.oz + s.shoal.z;
        const curR = Math.sqrt(p.x*p.x + p.y*p.y + p.z*p.z) || 1;
        const radF = (targetR - curR) / curR;
        p.vx += (p.x * radF * 0.6 + (ox - p.x) * 0.4) * s.homingStr * sphereFactor;
        p.vy += (p.y * radF * 0.6 + (oy - p.y) * 0.4) * s.homingStr * sphereFactor;
        p.vz += (p.z * radF * 0.6 + (oz - p.z) * 0.4) * s.homingStr * sphereFactor;

        // Pseudopod extrusions: pull surface cone of particles outward
        for (let pi = 0; pi < s.pseudopods.length; pi++) {
          const pod = s.pseudopods[pi];
          if (pod.strength < 0.01) continue;
          const dot = (p.x * pod.nx + p.y * pod.ny + p.z * pod.nz) / curR;
          if (dot > pod.cone) {
            const pull = (dot - pod.cone) / (1 - pod.cone);
            const outF = pull * pull * pod.strength * 0.90;
            p.vx += pod.nx * outF;
            p.vy += pod.ny * outF;
            p.vz += pod.nz * outF;
            p.energy = Math.min(1, p.energy + pull * 0.06);
          }
        }

        // Blob morph: autonomous organic shape deformation
        if (s.blobMorph.strength > 0.01) {
          const bm = s.blobMorph;
          const str = bm.strength;
          const ax = bm.axis.x, ay = bm.axis.y, az = bm.axis.z;
          const dot = p.x * ax + p.y * ay + p.z * az;
          if (bm.mode === 'elongate') {
            const perpX = p.x - dot * ax, perpY = p.y - dot * ay, perpZ = p.z - dot * az;
            const sgn = dot >= 0 ? 1 : -1;
            p.vx += (ax * sgn * 0.5 - perpX * 0.25) * str * 0.004;
            p.vy += (ay * sgn * 0.5 - perpY * 0.25) * str * 0.004;
            p.vz += (az * sgn * 0.5 - perpZ * 0.25) * str * 0.004;
          } else if (bm.mode === 'flatten') {
            p.vx -= ax * dot * str * 0.022;
            p.vy -= ay * dot * str * 0.022;
            p.vz -= az * dot * str * 0.022;
          } else if (bm.mode === 'lobe') {
            const lobeSep = Math.sin(bm.phase) * BASE_RADIUS * 0.42;
            const tgt = Math.sign(dot || 0.001) * lobeSep;
            p.vx += ax * (tgt - dot) * str * 0.011;
            p.vy += ay * (tgt - dot) * str * 0.011;
            p.vz += az * (tgt - dot) * str * 0.011;
          } else if (bm.mode === 'spin') {
            const crossX = ay * p.z - az * p.y;
            const crossY = az * p.x - ax * p.z;
            const crossZ = ax * p.y - ay * p.x;
            p.vx += crossX * str * 0.019;
            p.vy += crossY * str * 0.019;
            p.vz += crossZ * str * 0.019;
          }
        }

        // Logo formation: pull toward sampled path target, flatten z to 0
        if (lStr > 0.01 && p.logoTarget) {
          const ltx = p.logoTarget.x;
          const lty = p.logoTarget.y;
          p.vx += (ltx - p.x) * lStr * 0.055;
          p.vy += (lty - p.y) * lStr * 0.055;
          p.vz += (0   - p.z) * lStr * 0.12;

          // Mark particle as arrived so it can switch to logo color
          if (lStr > 0.5) {
            const dxt = p.x - ltx, dyt = p.y - lty;
            if (dxt*dxt + dyt*dyt + p.z*p.z < 500) p.atLogoTarget = true;
          }
        }
        if (lStr < 0.15) p.atLogoTarget = false;

        // PONG: pull each particle toward its stamped role position (paddle bar or ball cluster)
        if (pStr > 0.01 && p.pongRole) {
          const pg    = s.pong;
          const pongW = hw * 0.56;
          const pongH = hh * 0.70;
          const padH  = pongH * 0.70;
          const frac  = p.pongGroupN > 1 ? p.pongIdx / (p.pongGroupN - 1) : 0.5;
          let tx, ty;
          if (p.pongRole === 'left') {
            tx = -pongW + (p.pongIdx % 2) * 6;
            ty = pg.leftY + (frac - 0.5) * padH;
          } else if (p.pongRole === 'right') {
            tx = pongW  - (p.pongIdx % 2) * 6;
            ty = pg.rightY + (frac - 0.5) * padH;
          } else {
            const angle = (p.pongIdx / p.pongGroupN) * Math.PI * 2;
            const r = (p.pongIdx % 3 === 0) ? 5 : (p.pongIdx % 3 === 1) ? 12 : 20;
            tx = pg.bx + Math.cos(angle) * r;
            ty = pg.by + Math.sin(angle) * r;
          }
          p.vx += (tx - p.x) * 0.24 * pStr;
          p.vy += (ty - p.y) * 0.24 * pStr;
          p.vz += (0   - p.z) * 0.35 * pStr;
        }

        // ARKANOID: pull toward paddle / ball / brick target
        if (arkStr > 0.01 && p.arkRole !== null && p.arkRole !== undefined) {
          const ak   = s.arkanoid;
          const padW = w  * 0.10;
          const padY = hh * 0.72;
          let tx = 0, ty = 0, hasTx = true;
          if (p.arkRole === 'pad') {
            const frac = p.arkGroupN > 1 ? p.arkIdx / (p.arkGroupN - 1) : 0.5;
            tx = ak.paddleX + (frac - 0.5) * padW * 2;
            ty = padY + (p.arkIdx % 2) * 7;
          } else if (p.arkRole === 'ball') {
            const angle = (p.arkIdx / p.arkGroupN) * Math.PI * 2;
            const r = p.arkIdx < p.arkGroupN * 0.5 ? 5 : 14;
            tx = ak.bx + Math.cos(angle) * r;
            ty = ak.by + Math.sin(angle) * r;
          } else if (typeof p.arkRole === 'number' && ak.bricks?.[p.arkRole]?.alive) {
            const brick = ak.bricks[p.arkRole];
            const col   = (p.arkIdx % 3) - 1;
            const row   = Math.floor(p.arkIdx / 3) % 2 - 0.5;
            tx = brick.x + col * brick.hw * 0.55;
            ty = brick.y + row * brick.hh * 0.55;
          } else {
            hasTx = false; // released brick — drift freely
          }
          if (hasTx) {
            p.vx += (tx - p.x) * 0.24 * arkStr;
            p.vy += (ty - p.y) * 0.24 * arkStr;
            p.vz += (0   - p.z) * 0.35 * arkStr;
          }
        }

        // ── WALL-E scene: eye-tilt, shooting star, particle pull ──────────
        if (walleStr > 0.01 && p.walleRole) {
          const wl = s.walle;

          // Eye-tilt animation (updates once per frame, not per particle)
          if (i === 0 && walleStr > 0.3) {
            wl.eyePause--;
            if (wl.eyePause <= 0) {
              wl.eyeAngle = lerp(wl.eyeAngle, wl.eyeTarget, 0.018);
              if (Math.abs(wl.eyeAngle - wl.eyeTarget) < 0.03) {
                // Pick new target: tilt left/right with occasional straight-ahead
                const r = Math.random();
                wl.eyeTarget = r < 0.25 ? 0 : (r < 0.5 ? -0.3 : (r < 0.8 ? 0.3 : (Math.random()-0.5)*0.5));
                wl.eyePause = 90 + Math.random() * 160;
              }
            }

            // Shooting star timer
            wl.shootTimer--;
            if (wl.shootTimer <= 0 && !wl.shootActive) {
              wl.shootActive = true;
              wl.shootTimer = 90; // duration of streak
              // Give shooting-star particles a velocity burst
              for (let si = 0; si < ps.length; si++) {
                if (ps[si].walleRole === 'shoot') {
                  ps[si].vx += 12 + Math.random() * 3;
                  ps[si].vy += 4 + Math.random() * 2;
                  ps[si].energy = 1;
                }
              }
            }
            if (wl.shootActive) {
              wl.shootTimer--;
              if (wl.shootTimer <= 0) {
                wl.shootActive = false;
                wl.shootTimer = 480 + Math.random() * 360;
              }
            }
          }

          let tx = p.walleTarget.x;
          let ty = p.walleTarget.y;

          // Neck and Eyes rotation: neck counter-leans slightly while head tilts
          if (p.walleRole === 'eyes' || p.walleRole === 'neck') {
            const cvs2 = canvasRef.current;
            const sc2 = Math.min((cvs2?.width||800)/2, (cvs2?.height||600)/2) * 0.006;
            const wx2 = 25 * sc2, wy2 = 20 * sc2;
            const bodyT2 = wy2 - 14 * sc2;
            const neckBot2 = bodyT2, neckTop2 = wy2 - 52 * sc2;
            const ea = wl.eyeAngle;
            
            // Neck leans slightly to the opposite side of head tilt (organic balance)
            const na = -ea * 0.12; 
            const cosN = Math.cos(na), sinN = Math.sin(na);
            
            const nty = neckTop2 - neckBot2;

            if (p.walleRole === 'neck') {
              const dy = ty - neckBot2;
              tx = wx2 - dy * sinN;
              ty = neckBot2 + dy * cosN;
            } else {
              // Eyes rotate around the NEW neck top position
              const cosE = Math.cos(ea), sinE = Math.sin(ea);
              const dx = tx - wx2, dy = ty - neckTop2;
              tx = wx2 + dx * cosE - dy * sinE - (nty * sinN);
              ty = neckBot2 + dx * sinE + dy * cosE + (nty * cosN);
            }
          }

          // Stars: gentle energy twinkle
          if (p.walleRole === 'star') {
            p.energy = 0.3 + Math.sin(af * 0.04 + p.phase * 3) * 0.3;
          }

          // Body: subtle breathing (vertical oscillation)
          if (p.walleRole === 'body') {
            ty += Math.sin(af * 0.025 + p.phase) * 2;
          }

          // Glow: gentle flicker
          if (p.walleRole === 'glow') {
            p.energy = 0.15 + Math.sin(af * 0.03 + p.phase * 2) * 0.15;
          }

          // Shooting star: when not active, reset to start position
          if (p.walleRole === 'shoot' && !s.walle.shootActive) {
            tx = p.walleTarget.x;
            ty = p.walleTarget.y;
          }

          p.vx += (tx - p.x) * 0.14 * walleStr;
          p.vy += (ty - p.y) * 0.14 * walleStr;
          p.vz += (0  - p.z) * 0.28 * walleStr;
        }

        // ── MONOLITH scene: monolith hum, monkey movement, stick throw ──
        if (monoStr > 0.01 && p.monolithRole) {
          let tx = p.monolithTarget.x;
          let ty = p.monolithTarget.y;

          // Advance throw timer once per frame
          if (i === 0 && monoStr > 0.3) {
            const mo = s.monolith;
            if (mo) {
              mo.throwTimer--;
              if (mo.throwPhase === 'rest' && mo.throwTimer <= 0) {
                mo.throwPhase = 'flying';
                mo.stickVy = -10;
                mo.stickAngle = 0;
                mo.stickCenterY = mo.stickRestY;
                mo.throwTimer = 400;
              }
              if (mo.throwPhase === 'flying') {
                mo.stickVy += 0.13;
                mo.stickCenterY += mo.stickVy;
                mo.stickAngle += mo.stickAngleVel;
                if (mo.stickVy > 0 && mo.stickCenterY >= mo.stickRestY) {
                  mo.throwPhase = 'rest';
                  mo.stickCenterY = mo.stickRestY;
                  mo.stickVy = 0;
                  mo.stickAngle = 0;
                  mo.throwTimer = 480 + Math.random() * 360;
                }
              }
            }
          }

          // Monolith: very dark obsidian slab with barely-there hum
          if (p.monolithRole === 'monolith') {
            const hum = Math.sin(af * 0.015 + p.phase) * 0.5 + 0.5;
            p.energy = 0.04 + hum * 0.06;
            ty += Math.sin(af * 0.008 + p.phase) * 0.5;
          }

          // Stick: flies up slowly spinning during throw
          if (p.monolithRole === 'stick') {
            const mo = s.monolith;
            if (mo && mo.throwPhase === 'flying') {
              const cvs2 = canvasRef.current;
              const sc2 = Math.min((cvs2?.width||800)/2, (cvs2?.height||600)/2) * 0.006;
              const f = p.stickFraction || 0;
              tx = mo.stickCenterX + Math.cos(mo.stickAngle) * f * 28 * sc2;
              ty = mo.stickCenterY + Math.sin(mo.stickAngle) * f * 28 * sc2;
              p.energy = 0.85 + Math.sin(af * 0.1 + p.phase) * 0.15;
            } else {
              p.energy = 0.5;
            }
          }

          // Monkey: excited during throw, otherwise subtle sway
          if (p.monolithRole === 'monkey') {
            const mo = s.monolith;
            const exc = mo && mo.throwPhase === 'flying' ? 1 : 0;
            ty += Math.sin(af * (0.05 + exc * 0.05) + p.phase) * (3 + exc * 5);
            if (exc && Math.random() < 0.015) p.energy = 1;
            else if (Math.random() < 0.005) p.energy = 1;
          }

          // Bonfire sparks: shoot up and reset
          if (p.monolithRole === 'fire') {
            p.vy -= 0.65 + Math.random() * 0.45;
            p.vx += (Math.random() - 0.5) * 1.8;
            p.energy = 0.85 + Math.random() * 0.35;
            const cvs2 = canvasRef.current;
            const sc2 = Math.min((cvs2?.width||800)/2, (cvs2?.height||600)/2) * 0.006;
            const dy = p.y - ty;
            if (dy < -55 * sc2 || Math.random() < 0.06) {
              p.y = ty;
              p.x = tx + (Math.random() - 0.5) * 12 * sc2;
              p.vx = 0; p.vy = 0;
            }
            p.vx += (tx - p.x) * 0.01 * monoStr;
            p.vy += (ty - p.y) * 0.01 * monoStr;
          }

          if (p.monolithRole === 'rocks') p.energy = 0.1;
          if (p.monolithRole === 'star') p.energy = 0.3 + Math.sin(af * 0.04 + p.phase * 3) * 0.3;

          const mPull = p.monolithRole === 'monolith' ? 0.26 :
                        p.monolithRole === 'stick' && s.monolith?.throwPhase === 'flying' ? 0.30 : 0.14;
          p.vx += (tx - p.x) * mPull * monoStr;
          p.vy += (ty - p.y) * mPull * monoStr;
          p.vz += (0  - p.z) * 0.28 * monoStr;
        }

        // ── FORMATION scene: autonomous shape morphing ─────────────────
        if (frmStr > 0.01 && p.formationTarget) {
          const frm = s.currentFormation;
          let ftx = p.formationTarget.x, fty = p.formationTarget.y, ftz = p.formationTarget.z;

          // DNA helix & atom: continuous Y-axis rotation reveals 3-D depth
          if (frm === 'DNA_HELIX' || frm === 'ATOM') {
            const rot = af * (frm === 'DNA_HELIX' ? 0.013 : 0.010);
            const cosR = Math.cos(rot), sinR = Math.sin(rot);
            const nx = ftx * cosR - ftz * sinR;
            const nz = ftx * sinR + ftz * cosR;
            ftx = nx; ftz = nz;
          }

          // EKG: continuous scrolling monitor — every particle's y target updates each frame
          if (frm === 'EKG') {
            const scrollSpeed = 0.0055; // ~3 s per beat cycle
            const scrollPhase = (af * scrollSpeed) % 1;
            const baseNx = (p.formationTarget.x + 140) / 280; // 0..1 across width
            const lnx = (baseNx + scrollPhase) % 1;
            // Inline ECG waveform
            let cy = 0;
            if      (lnx < 0.10) cy =  Math.sin(lnx / 0.10 * Math.PI) * 12;
            else if (lnx < 0.28) cy =  0;
            else if (lnx < 0.33) cy = -Math.sin((lnx - 0.28) / 0.05 * Math.PI) * 8;
            else if (lnx < 0.38) cy =  Math.sin((lnx - 0.33) / 0.05 * Math.PI) * 90;
            else if (lnx < 0.43) cy = -Math.sin((lnx - 0.38) / 0.05 * Math.PI) * 14;
            else if (lnx < 0.56) cy =  0;
            else if (lnx < 0.72) cy =  Math.sin((lnx - 0.56) / 0.16 * Math.PI) * 28;
            fty = -cy;
            // Bright cursor sweeps with the active writing head
            const headPos = (1 - scrollPhase + 1) % 1;
            const dh = Math.min(Math.abs(baseNx - headPos), 1 - Math.abs(baseNx - headPos));
            p.energy = dh < 0.055 ? 1.0 - dh / 0.055 * 0.3 : 0.10;
          }

          // Dandelion: tree at rest sways gently; periodically seeds burst outward and drift up
          if (frm === 'BINARY_TREE') {
            const restDur = 300, floatDur = 130, cycleDur = restDur + floatDur;
            // Offset so burst never fires on frame 0
            const phase = (af + restDur) % cycleDur;
            const floating = phase < floatDur;

            if (p.formationRole === 'leaf') {
              if (phase === 0) {
                // Burst: seeds launch outward and slightly upward from their tip
                const ang = Math.atan2(p.formationTarget.y - 80, p.formationTarget.x) + (Math.random() - 0.5) * 1.3;
                const spd = 3.5 + Math.random() * 5;
                p.vx += Math.cos(ang) * spd;
                p.vy += Math.sin(ang) * spd * 0.45 - 1.2;
                p.energy = 1;
              }
              if (floating) {
                p.vy -= 0.055; // seeds float upward
                p.vx += (Math.random() - 0.5) * 0.10;
                p.energy = Math.max(p.energy * 0.975, 0.45);
                ftx = p.x; fty = p.y; ftz = p.z; // release homing so they drift freely
              } else {
                ftx += Math.sin(af * 0.032 + p.phase * 4) * 4;
                fty += Math.sin(af * 0.041 + p.phase * 3) * 2.5;
                p.energy = 0.25 + Math.sin(af * 0.05 + p.phase * 5) * 0.18;
              }
            } else if (p.formationRole === 'branch') {
              ftx += Math.sin(af * 0.018 + p.phase * 2) * 2;
              p.energy = 0.35;
            } else {
              p.energy = 0.5; // trunk
            }
          }

          // Role-based energy for DNA/ATOM (not handled by mode-specific blocks above)
          if (frm !== 'EKG' && frm !== 'BINARY_TREE') {
            if (p.formationRole === 'nucleus' || p.formationRole === 'strand0')
              p.energy = 0.55 + Math.sin(af * 0.03 + p.phase * 4) * 0.4;
            else if (p.formationRole === 'electron' || p.formationRole === 'strand1')
              p.energy = 0.3 + Math.sin(af * 0.05 + p.phase * 2) * 0.2;
            else if (p.formationRole === 'rung')
              p.energy = 0.2 + Math.sin(af * 0.025 + p.phase * 3) * 0.12;
          }

          p.vx += (ftx - p.x) * 0.12 * frmStr;
          p.vy += (fty - p.y) * 0.12 * frmStr;
          p.vz += (ftz - p.z) * 0.20 * frmStr;
        }

        // ── MATRIX scene: digital rain ──────────────────────────────────
        if (s.matrixStrength > 0.01 && s.matrix?.cols) {
          const ms = s.matrix;
          const FONT_SIZE = 22;
          const TRAIL_LENGTH = 28;
          const FLICKER_RATE = 150;
          const now = Date.now();

          if (i === 0) {
            if (s.matrixTimer > 0) {
              s.matrixTimer--;
              if (s.matrixTimer <= 0) s.explodingGlyphs = [];
            }
            
            if (s.matrixTimer <= 0) {
              ctx.save();
              ctx.globalCompositeOperation = 'lighter';
            
            ms.cols.forEach(col => {
              if (!col.isFading) {
                col.y += col.speed;
                if (col.y >= col.stopY || col.y > hh + (TRAIL_LENGTH * FONT_SIZE)) {
                  col.isFading = true;
                }
                const lastY = col.glyphs.length > 0 ? col.glyphs[col.glyphs.length - 1].yOffset : -Infinity;
                if (col.y - lastY >= FONT_SIZE) {
                  if (col.glyphs.length > 0) col.glyphs[col.glyphs.length - 1].isHead = false;
                  col.glyphs.push(ms.createGlyph(col.x, col.y, true));
                }
              } else {
                col.fadeAlpha *= 0.95;
                if (col.fadeAlpha < 0.01) {
                   col.y = Math.random() * -hh;
                   col.speed = 2 + Math.random() * 5;
                   col.stopY = 100 + Math.random() * (h + 8000);
                   col.isFading = false;
                   col.fadeAlpha = 1.0;
                   col.glyphs = [];
                }
              }

              if (col.glyphs.length > TRAIL_LENGTH) {
                col.glyphs.shift();
              }

              const mStr = s.matrixStrength;
              
              // Find the primary color for this column from its assigned Sentry particle
              const cp = ps.find(p => p.matrixColIdx === col.colIdx);
              const colRgb = hexToRgb(cp ? cp.color : '#00FF41');
              const mRgb = [0, 255, 65];
              const wRgb = [255, 255, 255];
              
              // Blend tail color from Sentry Native to Matrix Green
              const tr = Math.round(colRgb[0] + (mRgb[0] - colRgb[0]) * mStr);
              const tg = Math.round(colRgb[1] + (mRgb[1] - colRgb[1]) * mStr);
              const tb = Math.round(colRgb[2] + (mRgb[2] - colRgb[2]) * mStr);
              
              // Blend head color from Sentry Native to Matrix White
              const hr = Math.round(colRgb[0] + (wRgb[0] - colRgb[0]) * mStr);
              const hg = Math.round(colRgb[1] + (wRgb[1] - colRgb[1]) * mStr);
              const hb = Math.round(colRgb[2] + (wRgb[2] - colRgb[2]) * mStr);

              col.glyphs.forEach((glyph, idx) => {
                 if (!col.isFading && now - glyph.lastCharChange > FLICKER_RATE + Math.random() * 600) {
                    const nextShape = ms.getGlyphShape();
                    glyph.particles.forEach((gp, pIdx) => {
                      if (nextShape[pIdx]) {
                        gp.targetX = nextShape[pIdx].x * FONT_SIZE;
                        gp.targetY = nextShape[pIdx].y * FONT_SIZE;
                      }
                    });
                    glyph.lastCharChange = now;
                 }
                 
                 glyph.particles.forEach(gp => {
                    const worldTargetX = col.x + gp.targetX;
                    const worldTargetY = glyph.yOffset + gp.targetY;

                    // Pulsating wave: shockwave propagation (matching WALLE/MONOLITH hover)
                    for (let wi = 0; wi < wv.length; wi++) {
                      const ww = wv[wi];
                      const gpDist = Math.sqrt(gp.currX * gp.currX + gp.currY * gp.currY) || 1;
                      const dw = Math.abs(gpDist - ww.r);
                      if (dw < 30) {
                        const f = (1 - dw / 30) * ww.power * 0.55;
                        gp.vx += (gp.currX / gpDist) * f;
                        gp.vy += (gp.currY / gpDist) * f;
                      }
                    }

                    // Apply velocity and elastic homing to target
                    gp.currX += gp.vx;
                    gp.currY += gp.vy;
                    gp.vx *= 0.85; // friction
                    gp.vy *= 0.85;
                    gp.vx += (worldTargetX - gp.currX) * 0.08;
                    gp.vy += (worldTargetY - gp.currY) * 0.08;

                    const gx = hw + gp.currX;
                    const gy = hh + gp.currY;

                    ctx.beginPath();
                    ctx.arc(gx, gy, gp.size, 0, Math.PI * 2);
                    
                    if (glyph.isHead) {
                      ctx.fillStyle = `rgba(${hr}, ${hg}, ${hb}, ${col.fadeAlpha * mStr})`;
                      ctx.shadowBlur = 8 * col.fadeAlpha;
                      ctx.shadowColor = `rgb(${tr}, ${tg}, ${tb})`;
                    } else {
                      const trailPos = idx / col.glyphs.length;
                      const alpha = trailPos * col.fadeAlpha * mStr;
                      ctx.fillStyle = `rgba(${tr}, ${tg}, ${tb}, ${alpha})`;
                      ctx.shadowBlur = 0;
                    }
                    ctx.fill();
                 });
              });
              ctx.shadowBlur = 0; 
            });
            ctx.restore();
          }
        }

          if (p.matrixRole === 'matrix') {
            const col = ms.cols[p.matrixColIdx];
            if (col) {
              let tx = col.x;
              let ty = col.y;
              if (col.glyphs.length > 0) {
                const headGlyph = col.glyphs[col.glyphs.length - 1];
                if (headGlyph) {
                  const gp = headGlyph.particles[p.matrixDotIdx % headGlyph.particles.length];
                  if (gp) {
                    tx = col.x + gp.targetX;
                    ty = headGlyph.yOffset + gp.targetY;
                  }
                }
              }
              
              if (s.matrixStrength > 0.95 && !m.active) {
                p.x = tx;
                p.y = ty;
                p.z = 0;
                p.vx = 0;
                p.vy = 0;
                p.vz = 0;
              } else {
                p.vx += (tx - p.x) * 0.15 * s.matrixStrength; // Relaxed from 0.35 to allow organic bounce
                p.vy += (ty - p.y) * 0.15 * s.matrixStrength;
                p.vz += (0 - p.z) * 0.35 * s.matrixStrength; // Pull depth to flat 0
                
                // Physical heads respond to the cursor-entry shockwave via the
                // shared wave propagation loop below (matching WALLE/MONOLITH hover)
                // Moderate friction to allow wave propagation
                p.vx *= 0.88;
                p.vy *= 0.88;
                p.vz *= 0.88;
              }
              p.energy = 1.0;
            }
          }
        }

        // Boids: separation + alignment + cohesion
        let sepX = 0, sepY = 0, sepZ = 0, sepN = 0;
        let aliVx = 0, aliVy = 0, aliVz = 0;
        let cohX = 0, cohY = 0, cohZ = 0, cohN = 0;
        let eNbr = 0;

        if (doBoids) {
          for (let j = 0; j < ps.length; j++) {
            if (j === i) continue;
            const q  = ps[j];
            const dx = p.x - q.x, dy = p.y - q.y, dz = p.z - q.z;
            const dsq = dx*dx + dy*dy + dz*dz;
            if (dsq < boidRadSq) {
              cohX += q.x; cohY += q.y; cohZ += q.z;
              aliVx += q.vx; aliVy += q.vy; aliVz += q.vz;
              cohN++;
              if (dsq < sepRadSq && dsq > 0.01) {
                const nd = Math.sqrt(dsq);
                const f  = 1 - nd / Math.sqrt(sepRadSq);
                sepX += (dx / nd) * f; sepY += (dy / nd) * f; sepZ += (dz / nd) * f;
                sepN++;
              }
              eNbr = Math.max(eNbr, q.energy * 0.65);
            }
          }
          if (cohN > 0) {
            p.vx += (cohX/cohN - p.x) * s.boidCoh * 0.012 + (aliVx/cohN) * s.boidAli * 0.055;
            p.vy += (cohY/cohN - p.y) * s.boidCoh * 0.012 + (aliVy/cohN) * s.boidAli * 0.055;
            p.vz += (cohZ/cohN - p.z) * s.boidCoh * 0.012 + (aliVz/cohN) * s.boidAli * 0.055;
          }
          if (sepN > 0) {
            p.vx += sepX * s.boidSep * 0.055;
            p.vy += sepY * s.boidSep * 0.055;
            p.vz += sepZ * s.boidSep * 0.055;
          }
        }

        // Shockwave impulses
        for (let wi = 0; wi < wv.length; wi++) {
          const ww   = wv[wi];
          const dist = Math.sqrt(p.x*p.x + p.y*p.y + p.z*p.z);
          const dw   = Math.abs(dist - ww.r);
          if (dw < 30) {
            const f  = (1 - dw / 30) * ww.power;
            const nd = dist || 1;
            p.vx += (p.x/nd) * f; p.vy += (p.y/nd) * f; p.vz += (p.z/nd) * f;
            p.energy = Math.min(1, p.energy + f * 0.12);
          }
        }

        // Layered-sine organic noise drift
        const nScale = 1.2 + p.energy * 2.8;
        p.vx += noise1(t, p.ns[0]) * nScale * 0.009;
        p.vy += noise1(t, p.ns[1]) * nScale * 0.009;
        p.vz += noise1(t, p.ns[2]) * nScale * 0.009;

        // Mouse: organism fear response — flinch, flee, tremble (suppressed during games/scenes)
        if (m.active && pStr < 0.5 && arkStr < 0.5 && walleStr < 0.5 && monoStr < 0.5 && s.matrixStrength < 0.5 && frmStr < 0.5) {
          const mx3 = m.x * 0.55, my3 = m.y * 0.55;
          const dx = p.x - mx3, dy = p.y - my3;
          const distSq = dx*dx + dy*dy;
          const dist = Math.sqrt(distSq) || 1;
          const sens = p.sensitivity;

          if (distSq < 100000) {
            // Repulsion: quadratic falloff — sharp flinch near cursor, fades with distance
            const proximity = Math.max(0, 1 - dist / 316);
            const repelF = proximity * proximity * 3.2 * sens;
            p.vx += (dx / dist) * repelF;
            p.vy += (dy / dist) * repelF;

            // Fear trembling: random agitation scaled by proximity
            const tremble = proximity * 1.5 * sens;
            p.vx += (Math.random() - 0.5) * tremble;
            p.vy += (Math.random() - 0.5) * tremble;

            // Vortex: circular mouse motion creates a tangential swirl field
            const angVel = m.angularVel;
            if (Math.abs(angVel) > 0.05) {
              const mcDist = Math.sqrt(m.x * m.x + m.y * m.y) || 1;
              const swX = -m.y / mcDist;
              const swY =  m.x / mcDist;
              p.vx += swX * angVel * proximity * 7 * sens;
              p.vy += swY * angVel * proximity * 7 * sens;
            }

            p.energy = Math.min(1, p.energy + proximity * 0.08);
          }

          // Fast-cursor shockwave: sharp movement disturbs nearby particles
          if (m.speed > 4 && distSq < 25000) {
            const shock = (m.speed - 4) * 0.12 * sens;
            p.vx += (dx / dist) * shock + (Math.random() - 0.5) * shock;
            p.vy += (dy / dist) * shock + (Math.random() - 0.5) * shock;
            p.energy = Math.min(1, p.energy + 0.3);
          }
        }

        // RECOVERING: strong pull back to shell
        if (s.mode === 'RECOVERING') {
          p.vx += (p.ox - p.x) * 0.07;
          p.vy += (p.oy - p.y) * 0.07;
          p.vz += (p.oz - p.z) * 0.07;
        }

        // Fish-school alignment: all particles lean into the shoal's direction of travel,
        // causing the organism to stretch forward and compress behind like a real school.
        p.vx += s.shoal.vx * 0.22;
        p.vy += s.shoal.vy * 0.22;
        p.vz += s.shoal.vz * 0.14;

        // Energy contagion + decay
        p.energy = Math.max(p.energy * 0.972, eNbr);
        sumEnergy += p.energy;

        // Integrate
        p.vx *= s.friction; p.vy *= s.friction; p.vz *= s.friction;
        p.x  += p.vx;       p.y  += p.vy;       p.z  += p.vz;

        // 3-D projection
        const rx1 =  p.x * cosY + p.z * sinY;
        const rz1 = -p.x * sinY + p.z * cosY;
        const ry2 =  p.y * cosX - rz1 * sinX;
        const rz2 =  p.y * sinX + rz1 * cosX;
        p.scale = PERSPECTIVE / (PERSPECTIVE + rz2);
        p.x2d   = rx1 * p.scale + hw;
        p.y2d   = ry2 * p.scale + hh;
      }

      s.globalEnergy = sumEnergy / ps.length;
      const ge = s.globalEnergy;

      if (m.active && pStr < 0.5 && arkStr < 0.5 && walleStr < 0.5 && monoStr < 0.5 && s.matrixStrength < 0.5 && frmStr < 0.5) {
        // Collective flee: whole organism drifts away from cursor position
        const fleeX = -m.x * 0.00055, fleeY = -m.y * 0.00055;
        for (let i = 0; i < ps.length; i++) {
          ps[i].vx += fleeX;
          ps[i].vy += fleeY;
        }

        // Panic startle: random particles shoot off (~3× per second)
        if (Math.random() < 0.05) {
          const pj = ps[Math.floor(Math.random() * ps.length)];
          const ang = Math.random() * Math.PI * 2;
          const str = 4 + Math.random() * 7;
          pj.vx += Math.cos(ang) * str;
          pj.vy += Math.sin(ang) * str;
          pj.energy = Math.min(1, pj.energy + 0.7);
        }

        // Periodic panic ripple through entire organism (~every 3–6 s)
        if (Math.random() < 0.003) {
          waves.current.push({ r: 0, speed: 11, power: 1.8 });
        }
      }

      // ── Synapses ────────────────────────────────────────────────────────
      segCount.fill(0);
      const jitter = 0.5 + ge * 5 + (m.active ? 3 : 0);
      const md     = s.synapseMaxDist;
      const mdSq   = md * md;
      const synStep = m.active ? 2 : 3;

      for (let i = 0; i < ps.length; i++) {
        const p1 = ps[i];
        if (p1.scale < 0.5) continue;
        const ci  = COLOR_IDX[p1.color];
        const buf = segData[ci];
        for (let j = i + 1; j < ps.length; j += synStep) {
          const p2 = ps[j];
          const dx = p1.x - p2.x, dy = p1.y - p2.y, dz = p1.z - p2.z;
          const distSq = dx*dx + dy*dy + dz*dz;
          if (distSq < mdSq) {
            // In LOGO mode, skip connections whose midpoint falls in empty space
            if (lStr > 0.7 && logoHitRef.current) {
              const lh = logoHitRef.current;
              const mx = (p1.x + p2.x) * 0.5 / lh.scale + LOGO_SVG_W * 0.5;
              const my = (p1.y + p2.y) * 0.5 / lh.scale + LOGO_SVG_H * 0.5;
              if (!lh.ctx.isPointInPath(lh.path, mx, my)) continue;
            }
            const dist   = Math.sqrt(distSq);
            const eBoost = 1 + (p1.energy + p2.energy) * 1.8;
            const alpha  = Math.min(1, (1 - dist / md) * s.synapseAlpha * p1.scale * eBoost);
            const off    = segCount[ci] * 5;
            if (off + 5 <= buf.length) {
              buf[off]   = p1.x2d + (Math.random() - 0.5) * jitter;
              buf[off+1] = p1.y2d + (Math.random() - 0.5) * jitter;
              buf[off+2] = p2.x2d + (Math.random() - 0.5) * jitter * 0.4;
              buf[off+3] = p2.y2d + (Math.random() - 0.5) * jitter * 0.4;
              buf[off+4] = alpha;
              segCount[ci]++;
            }
          }
        }
      }

      ctx.lineWidth = 0.5 + ge * 1.0;
      for (let ci = 0; ci < 4; ci++) {
        const cnt = segCount[ci];
        if (cnt === 0) continue;
        ctx.strokeStyle = lStr > 0.6 ? LOGO_COLORS[ci] : NEURAL_COLORS[ci];
        const buf = segData[ci];
        for (let k = 0; k < cnt; k++) {
          const off = k * 5;
          ctx.globalAlpha = buf[off+4];
          ctx.beginPath();
          ctx.moveTo(buf[off],   buf[off+1]);
          ctx.lineTo(buf[off+2], buf[off+3]);
          ctx.stroke();
        }
      }

      // ── Nodes ────────────────────────────────────────────────────────────
      ps.sort((a, b) => b.scale - a.scale);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      for (let i = 0; i < ps.length; i++) {
        const p   = ps[i];
        const ex  = p.energy;
        const op  = Math.max(0, Math.min(0.92, p.scale * (0.65 + ex * 0.28)));
        // Clamp radius ≥ 0: a particle crossing behind the camera plane yields a
        // large negative perspective scale, which would throw on ctx.arc()/lineWidth.
        const r   = Math.max(0, Math.min(2.8, p.size * p.scale * (0.55 + ex * 0.45)));
        const spd = Math.sqrt(p.vx*p.vx + p.vy*p.vy);

        const mStr = s.matrixStrength;
        const isMat = mStr > 0.01 && p.matrixRole === 'matrix';
        const baseColor = p.atLogoTarget ? p.logoColor : p.color;
        
        let drawColor = baseColor;
        if (isMat) {
           const c1 = hexToRgb(baseColor);
           const c2 = [0, 255, 65]; // Matrix green
           const r = Math.round(c1[0] + (c2[0] - c1[0]) * mStr);
           const g = Math.round(c1[1] + (c2[1] - c1[1]) * mStr);
           const b = Math.round(c1[2] + (c2[2] - c1[2]) * mStr);
           drawColor = `rgb(${r},${g},${b})`;
        }
        
        // Sentry particles NEVER fade out during Matrix anymore.
        // They are always 100% visible, smoothly blending their colors to Matrix Green.
        ctx.globalAlpha = op;
        ctx.fillStyle   = drawColor;

        // Velocity tail: thin elongated stroke in motion direction
        if (spd > 0.6) {
          const angle = Math.atan2(p.vy, p.vx);
          const tLen  = isMat ? spd * 0.55 : spd * 2.0; // Shorten tails for Matrix mode
          ctx.globalAlpha = op * 0.55;
          ctx.lineWidth   = r * 0.9;
          ctx.strokeStyle = drawColor;
          ctx.setTransform(Math.cos(angle), Math.sin(angle), -Math.sin(angle), Math.cos(angle), p.x2d, p.y2d);
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(-tLen, 0);
          ctx.stroke();
          ctx.globalAlpha = op;
        }

        // Core dot
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.beginPath();
        ctx.arc(p.x2d, p.y2d, r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.setTransform(1, 0, 0, 1, 0, 0);
    };

    const start = () => { if (animId !== null) return; animId = requestAnimationFrame(render); };
    const stop  = () => { if (animId !== null) { cancelAnimationFrame(animId); animId = null; } };
    startRef.current = start;
    stopRef.current  = stop;
    if (visibleRef.current) start();
    return stop;
  }, []); // eslint-disable-line

  useEffect(() => {
    visibleRef.current = visible;
    if (visible) {
      startRef.current?.();
    } else {
      const t = setTimeout(() => stopRef.current?.(), 900);
      return () => clearTimeout(t);
    }
  }, [visible]);

  useEffect(() => {
    const handler = (e) => {
      const mode = e.detail;
      if (mode) transitionTo(mode);
    };
    window.addEventListener('nw:sentryTrigger', handler);
    return () => window.removeEventListener('nw:sentryTrigger', handler);
  }, []); // eslint-disable-line

  useEffect(() => {
    const onInput = (e) => {
      const isTouch = e.type.startsWith('touch');
      const point = isTouch ? (e.touches[0] || e.changedTouches[0]) : e;
      if (!point && !isTouch) return;

      if (isTouch && (e.type === 'touchend' || e.type === 'touchcancel')) {
        mouse.current.active = false;
        mouse.current.leaveTimer = 180;
        return;
      }

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const clientX = point.clientX;
      const clientY = point.clientY;

      if (!isTouch && excludeRef?.current?.contains(e.target)) {
        if (mouse.current.active) {
          mouse.current.active = false;
          mouse.current.leaveTimer = 180;
        }
        return;
      }

      const inside = clientX >= rect.left && clientX <= rect.right &&
                     clientY >= rect.top  && clientY <= rect.bottom;

      if (inside) {
        const wasActive = mouse.current.active;
        const nx = clientX - rect.left - rect.width  / 2;
        const ny = clientY - rect.top  - rect.height / 2;

        const curMode = state.current.mode;
        const isGameMode = curMode === 'PONG' || curMode === 'ARKANOID' || curMode === 'MATRIX';

        if (!isGameMode) {
          const sh = state.current.shoal;
          const fromOrg = Math.sqrt((nx - sh.x) ** 2 + (ny - sh.y) ** 2);
          if (fromOrg > BASE_RADIUS * 1.5) {
            if (mouse.current.active) {
              mouse.current.active = false;
              mouse.current.leaveTimer = 180;
            }
            return;
          }
        }

        // Prevent scrolling on mobile during interaction
        if (isTouch && e.type === 'touchmove') {
          e.preventDefault();
        }

        const dx = nx - mouse.current.x, dy = ny - mouse.current.y;
        mouse.current.speed = Math.sqrt(dx * dx + dy * dy);
        const newAngle = Math.atan2(ny, nx);

        if (mouse.current.active) {
          let dA = newAngle - mouse.current.prevAngle;
          if (dA > Math.PI) dA -= 2 * Math.PI;
          if (dA < -Math.PI) dA += 2 * Math.PI;
          mouse.current.angularVel = mouse.current.angularVel * 0.78 + dA * 0.22;
        }
        mouse.current.prevAngle = newAngle;
        mouse.current.x = nx;
        mouse.current.y = ny;
        mouse.current.active = true;
        mouse.current.leaveTimer = 0;

        if (!wasActive) {
          const ps = particles.current;
          const mx3 = mouse.current.x * 0.55;
          const my3 = mouse.current.y * 0.55;
          waves.current.push({ r: 0, speed: 14, power: 2.8 });
          for (let i = 0; i < ps.length; i++) {
            const p = ps[i];
            const dxp = p.x - mx3, dyp = p.y - my3;
            const distSq = dxp * dxp + dyp * dyp;
            if (distSq < 30000) {
              const dist = Math.sqrt(distSq) || 1;
              const impulse = (1 - dist / 174) * 5 * p.sensitivity;
              p.vx += (dxp / dist) * impulse;
              p.vy += (dyp / dist) * impulse;
              p.energy = Math.min(1, p.energy + 0.6);
            }
          }
        }
      } else if (mouse.current.active) {
        mouse.current.active = false;
        mouse.current.leaveTimer = 180;
      }
    };

    document.addEventListener('mousemove', onInput);
    document.addEventListener('touchstart', onInput, { passive: false });
    document.addEventListener('touchmove', onInput, { passive: false });
    document.addEventListener('touchend', onInput);
    document.addEventListener('touchcancel', onInput);

    return () => {
      document.removeEventListener('mousemove', onInput);
      document.removeEventListener('touchstart', onInput);
      document.removeEventListener('touchmove', onInput);
      document.removeEventListener('touchend', onInput);
      document.removeEventListener('touchcancel', onInput);
    };
  }, [excludeRef]);

  return (
    <div
      className="absolute top-0 left-0 right-0 pointer-events-none"
      style={{ bottom: '180px', opacity: visible ? 1 : 0, transition: 'opacity 800ms ease' }}
    >
      <canvas ref={canvasRef} className="w-full h-full" style={{ display: 'block' }} />
    </div>
  );
};

export default NeuralSentry;
